import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  ColorPicker,
  Empty,
  Input,
  InputNumber,
  message,
  Modal,
  Progress,
  Row,
  Segmented,
  Select,
  Slider,
  Space,
  Spin,
  Steps,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import {
  AppstoreOutlined,
  BgColorsOutlined,
  BulbOutlined,
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  FileImageOutlined,
  FolderOpenOutlined,
  HighlightOutlined,
  InboxOutlined,
  PictureOutlined,
  PlusOutlined,
  ReloadOutlined,
  RocketOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useHistory } from "react-router-dom";
import config from "../../api_config.json";
import { useAppContext } from "../../context";
import { useAuth0 } from "../../react-auth0-spa";
import { compareByOrderAndName, getModuleCategory } from "./moduleCatalogUtils";

const { Text, Title } = Typography;
const { TextArea } = Input;

const styles = `
  .icons-page {
    max-width: 1640px;
    margin: 0 auto;
    color: #18272f;
  }

  .icons-head {
    display: flex;
    justify-content: space-between;
    gap: 24px;
    padding: 26px 28px;
    margin-bottom: 18px;
    border: 1px solid rgba(24, 39, 47, 0.08);
    border-radius: 18px;
    background:
      linear-gradient(135deg, rgba(246, 248, 249, 0.98), rgba(239, 249, 248, 0.92)),
      radial-gradient(circle at 14% 22%, rgba(236, 102, 78, 0.18), transparent 26%),
      radial-gradient(circle at 88% 18%, rgba(23, 119, 147, 0.13), transparent 22%);
    box-shadow: 0 18px 44px rgba(24, 39, 47, 0.07);
  }

  .icons-head-copy {
    min-width: 0;
  }

  .icons-head-title {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .icons-head-mark {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    color: #ffffff;
    background: #195c7e;
    box-shadow: inset 0 -10px 20px rgba(0, 0, 0, 0.14);
  }

  .icons-head-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(110px, 1fr));
    gap: 10px;
    min-width: 360px;
  }

  .icons-stat {
    padding: 13px 14px;
    border: 1px solid rgba(24, 39, 47, 0.08);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.76);
  }

  .icons-layout {
    display: block;
  }

  .icons-library,
  .icons-builder {
    border: 1px solid rgba(24, 39, 47, 0.08);
    border-radius: 18px;
    background: #ffffff;
    box-shadow: 0 16px 36px rgba(24, 39, 47, 0.06);
  }

  .icons-library {
    overflow: hidden;
  }

  .icons-builder {
    padding: 18px;
  }

  .icons-builder-modal .icons-builder {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0;
    border: 0;
    box-shadow: none;
  }

  .icons-builder-modal .ant-modal-body {
    max-height: calc(100vh - 160px);
    overflow: auto;
    padding-right: 2px;
  }

  .icons-builder-main {
    display: grid;
    grid-template-columns: minmax(260px, 360px) minmax(0, 1fr);
    gap: 18px;
    align-items: start;
  }

  .icons-preview-col {
    position: sticky;
    top: 0;
    align-self: start;
  }

  .icons-builder-modal .icons-preview-zone {
    grid-template-columns: 1fr;
    gap: 18px;
    min-height: 360px;
  }

  .icons-builder-modal .icons-preview-large svg {
    width: min(100%, 276px);
  }

  .icons-builder-modal .icons-preview-small svg {
    width: 88px;
    height: 88px;
  }

  .icons-section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }

  .icons-source-hint {
    display: block;
    margin-top: 10px;
  }

  .icons-builder-actions {
    margin-top: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .icons-builder-actions .ant-btn {
    width: 100%;
  }

  .icons-builder-steps {
    margin-bottom: 4px;
  }

  .icons-builder-tabs {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .icons-builder-nav {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 18px;
  }

  .icons-preview-toggle {
    display: flex;
    justify-content: center;
    margin-top: 12px;
  }

  .icons-tab-stack {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .icons-preview-glyph-empty {
    color: rgba(25, 92, 126, 0.4);
    font-size: 28px;
  }

  .icons-preview-note {
    display: block;
    margin-top: 12px;
    text-align: center;
  }

  .icons-source-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .icons-source-grid .ant-btn {
    height: 44px;
  }

  .icons-toolbar {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) 200px auto auto;
    gap: 10px;
    padding: 16px;
    border-bottom: 1px solid rgba(24, 39, 47, 0.07);
    background: #fbfcfc;
  }

  .icons-category-section {
    padding: 18px;
    border-bottom: 1px solid rgba(24, 39, 47, 0.06);
  }

  .icons-category-section:last-child {
    border-bottom: 0;
  }

  .icons-category-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .icons-category-main {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .icons-category-swatch,
  .icons-mini-swatch {
    width: 18px;
    height: 18px;
    border-radius: 6px;
    border: 1px solid rgba(24, 39, 47, 0.12);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.42);
  }

  .icons-mini-swatch {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    border-radius: 5px;
  }

  .icons-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(190px, 100%), 1fr));
    gap: 14px;
  }

  .icons-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    min-height: 212px;
    padding: 18px 14px 15px;
    border: 1px solid rgba(24, 39, 47, 0.08);
    border-radius: 14px;
    background: linear-gradient(180deg, #ffffff, #fafbfb);
    transition: transform 150ms ease, border-color 150ms ease, box-shadow 150ms ease;
  }

  .icons-card:hover {
    transform: translateY(-1px);
    border-color: rgba(25, 92, 126, 0.2);
    box-shadow: 0 12px 24px rgba(24, 39, 47, 0.08);
  }

  .icons-module-icon {
    display: grid;
    place-items: center;
    width: 148px;
    height: 148px;
    border-radius: 24px;
  }

  .icons-module-icon img {
    width: 132px;
    height: 132px;
    object-fit: contain;
  }

  .icons-module-icon--empty {
    background: #f7f9fa;
    border: 1px dashed rgba(24, 39, 47, 0.18);
  }

  .icons-module-empty {
    width: 92px;
    height: 92px;
    border-radius: 22px;
    background: linear-gradient(135deg, rgba(25, 92, 126, 0.16), rgba(236, 102, 78, 0.13));
  }

  .icons-module-missing {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    text-align: center;
    color: rgba(24, 39, 47, 0.4);
  }

  .icons-module-missing-icon {
    font-size: 34px;
  }

  .icons-module-missing-label {
    font-size: 12px;
    font-weight: 600;
    line-height: 1.25;
  }

  .icons-module-copy {
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 10px;
  }

  .icons-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .icons-module-name {
    width: 100%;
    min-height: 38px;
    display: -webkit-box;
    overflow: hidden;
    white-space: normal;
    overflow-wrap: anywhere;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    font-size: 13px;
    line-height: 1.35;
  }

  .icons-module-name--link {
    cursor: pointer;
    transition: color 120ms ease;
  }

  .icons-module-name--link:hover {
    color: #195c7e;
    text-decoration: underline;
  }

  .icons-module-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .icons-module-edit {
    width: 100%;
  }

  .icons-module-tools {
    display: flex;
    justify-content: center;
    gap: 6px;
  }

  .icons-module-tools .ant-btn {
    display: inline-grid;
    place-items: center;
    width: 30px;
    height: 30px;
    padding: 0;
  }

  .icons-preview-zone {
    display: grid;
    grid-template-columns: 1fr 96px;
    gap: 14px;
    align-items: center;
    padding: 14px;
    border-radius: 14px;
    background:
      linear-gradient(45deg, rgba(24, 39, 47, 0.04) 25%, transparent 25%),
      linear-gradient(-45deg, rgba(24, 39, 47, 0.04) 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, rgba(24, 39, 47, 0.04) 75%),
      linear-gradient(-45deg, transparent 75%, rgba(24, 39, 47, 0.04) 75%);
    background-size: 22px 22px;
    background-position: 0 0, 0 11px, 11px -11px, -11px 0;
    border: 1px solid rgba(24, 39, 47, 0.08);
  }

  .icons-preview-large,
  .icons-preview-small {
    display: grid;
    place-items: center;
  }

  .icons-preview-large svg {
    width: min(100%, 216px);
    height: auto;
    filter: drop-shadow(0 18px 26px rgba(24, 39, 47, 0.18));
  }

  .icons-preview-small svg {
    width: 72px;
    height: 72px;
  }

  .icons-field-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 7px;
  }

  .icons-two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .icons-model-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .icons-ai-modal-grid {
    display: grid;
    grid-template-columns: 320px minmax(0, 1fr);
    gap: 16px;
    align-items: start;
  }

  .icons-ai-compose,
  .icons-ai-history-panel {
    border: 1px solid rgba(24, 39, 47, 0.08);
    border-radius: 14px;
    background: #ffffff;
  }

  .icons-ai-compose {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px;
  }

  .icons-ai-context-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .icons-ai-field {
    display: flex;
    flex-direction: column;
  }

  .icons-ai-model-format {
    display: inline-block;
    margin-left: 8px;
    padding: 0 7px;
    border-radius: 6px;
    font-size: 11px;
    line-height: 18px;
    color: #4a5a62;
    background: rgba(24, 39, 47, 0.06);
    vertical-align: middle;
  }

  .icons-ai-toggle {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    border: 1px solid rgba(24, 39, 47, 0.08);
    border-radius: 12px;
    background: #f8fafb;
  }

  .icons-ai-toggle-main {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .icons-ai-toggle-copy {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .icons-ai-modal .ant-modal-body {
    max-height: calc(100vh - 140px);
    overflow: auto;
  }

  .icons-ai-wizard {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .icons-ai-steps {
    margin-bottom: 2px;
  }

  .icons-ai-step {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .icons-ai-step-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .icons-ai-improve {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  .icons-ai-system > summary {
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: #195c7e;
  }

  .icons-ai-system-hint {
    display: block;
    margin: 8px 0;
  }

  .icons-ai-advanced {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 10px;
  }

  .icons-apply-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .icons-apply-opt-thumb {
    display: inline-grid;
    place-items: center;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    overflow: hidden;
    background: #f3f6f7;
  }

  .icons-apply-opt-thumb img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .icons-apply-opt-thumb .icons-module-empty {
    width: 16px;
    height: 16px;
    border-radius: 5px;
  }

  .icons-apply-confirm {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .icons-apply-compare {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
  }

  .icons-apply-side {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }

  .icons-apply-thumb {
    display: grid;
    place-items: center;
    width: 92px;
    height: 92px;
    border-radius: 16px;
    border: 1px solid rgba(24, 39, 47, 0.08);
    background: #f3f6f7;
    overflow: hidden;
  }

  .icons-apply-thumb img,
  .icons-apply-thumb svg {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .icons-apply-none {
    font-size: 12px;
    color: #8a979e;
  }

  .icons-apply-arrow {
    font-size: 22px;
    color: #8a979e;
  }

  .icons-apply-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  .icons-ai-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 4px;
  }

  .icons-ai-history-foot {
    display: flex;
    justify-content: flex-end;
  }

  .icons-ai-context {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f7fafb;
    border: 1px solid rgba(24, 39, 47, 0.08);
  }

  .icons-ai-status {
    padding: 12px;
    border-radius: 12px;
    background: #f8fafb;
    border: 1px solid rgba(24, 39, 47, 0.08);
  }

  .icons-ai-status-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }

  .icons-ai-status .ant-steps {
    margin-top: 4px;
  }

  .icons-ai-estimate,
  .icons-ai-progress {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f8fafb;
    border: 1px solid rgba(24, 39, 47, 0.08);
  }

  .icons-ai-progress {
    margin: 10px 0 0;
    padding: 0;
    border: 0;
    background: transparent;
  }

  .icons-ai-estimate-row,
  .icons-ai-progress-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
  }

  .icons-ai-run-progress {
    margin: 10px 0;
  }

  .icons-ai-run-actions {
    flex: 0 0 auto;
  }

  .icons-ai-meta {
    display: block;
    margin-top: 8px;
    font-size: 11px;
  }

  .icons-ai-history-panel {
    max-height: min(660px, 72vh);
    overflow: auto;
  }

  .icons-ai-history-head {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    border-bottom: 1px solid rgba(24, 39, 47, 0.07);
    background: rgba(255, 255, 255, 0.96);
  }

  .icons-ai-run-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
  }

  .icons-ai-run {
    padding: 12px;
    border: 1px solid rgba(24, 39, 47, 0.08);
    border-radius: 12px;
    background: #fbfcfc;
  }

  .icons-ai-run--hero {
    background: #ffffff;
    border-color: rgba(25, 92, 126, 0.35);
    box-shadow: 0 10px 24px rgba(24, 39, 47, 0.08);
  }

  .icons-ai-run--hero .icons-ai-response-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }

  .icons-ai-older {
    border-top: 1px dashed rgba(24, 39, 47, 0.12);
    padding-top: 12px;
  }

  .icons-ai-older > summary {
    cursor: pointer;
    margin-bottom: 12px;
    color: #195c7e;
    font-weight: 600;
  }

  .icons-ai-run-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }

  .icons-ai-prompt {
    display: block;
    margin: 8px 0 10px;
    color: #2c3b42;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .icons-ai-response-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(124px, 1fr));
    gap: 10px;
  }

  .icons-ai-result-card {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px;
    border: 1px solid rgba(24, 39, 47, 0.08);
    border-radius: 10px;
    background: #ffffff;
  }

  .icons-ai-result-thumb {
    display: grid;
    place-items: center;
    aspect-ratio: 1;
    border-radius: 8px;
    overflow: hidden;
    background:
      linear-gradient(45deg, rgba(24, 39, 47, 0.05) 25%, transparent 25%),
      linear-gradient(-45deg, rgba(24, 39, 47, 0.05) 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, rgba(24, 39, 47, 0.05) 75%),
      linear-gradient(-45deg, transparent 75%, rgba(24, 39, 47, 0.05) 75%);
    background-size: 18px 18px;
    background-position: 0 0, 0 9px, 9px -9px, -9px 0;
  }

  .icons-ai-result-thumb img {
    width: 86%;
    height: 86%;
    object-fit: contain;
  }

  .icons-ai-thumb-fallback {
    display: grid;
    place-items: center;
    gap: 6px;
    color: #697982;
    font-size: 12px;
    text-align: center;
  }

  .icons-ai-details {
    margin-top: 10px;
    color: #697982;
  }

  .icons-ai-details summary {
    cursor: pointer;
    font-size: 12px;
  }

  .icons-ai-details pre {
    max-height: 240px;
    overflow: auto;
    margin: 8px 0 0;
    padding: 10px;
    border-radius: 8px;
    background: #18272f;
    color: #edf6f8;
    font-size: 11px;
    line-height: 1.45;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .icons-asset-strip {
    display: flex;
    gap: 10px;
    align-items: center;
    min-width: 0;
    padding: 10px;
    border-radius: 12px;
    background: #f8fafb;
    border: 1px solid rgba(24, 39, 47, 0.08);
  }

  .icons-asset-thumb {
    display: grid;
    place-items: center;
    width: 46px;
    height: 46px;
    border-radius: 10px;
    background: #ffffff;
    border: 1px solid rgba(24, 39, 47, 0.08);
    overflow: hidden;
  }

  .icons-asset-thumb img {
    width: 38px;
    height: 38px;
    object-fit: contain;
  }

  .icons-empty-state {
    padding: 52px 18px;
  }

  @media (max-width: 1180px) {
    .icons-layout {
      grid-template-columns: 1fr;
    }

    .icons-builder {
      position: static;
    }
  }

  @media (max-width: 760px) {
    .icons-head,
    .icons-category-title {
      flex-direction: column;
      align-items: stretch;
    }

    .icons-head-stats,
    .icons-toolbar,
    .icons-two-col {
      grid-template-columns: 1fr;
      min-width: 0;
    }

    .icons-preview-zone {
      grid-template-columns: 1fr;
    }

    .icons-source-grid {
      grid-template-columns: 1fr;
    }

    .icons-ai-modal-grid {
      grid-template-columns: 1fr;
    }

    .icons-ai-history-panel {
      max-height: none;
    }
  }

  @media (max-width: 920px) {
    .icons-builder-main {
      grid-template-columns: 1fr;
    }

    .icons-preview-col {
      position: static;
    }

    .icons-builder-modal .icons-preview-zone {
      min-height: 0;
    }

    .icons-source-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const MODULE_ICONS_QUERY = `
  query fetchModuleIcons($where: ModuleWhereInput) {
    modules(where: $where, first: 10000) {
      edges {
        node {
          objectId
          name
          order
          icon {
            name
            url
          }
          module_category_pointer {
            objectId
            name
            order
            color
            darker_color
          }
        }
      }
    }
  }
`
  .replace(/\r?\n|\r/g, "")
  .replace(/\t/g, " ");

const UPDATE_MODULE_ICON_MUTATION = `
  mutation UpdateModuleIcon($id: ID!, $fields: UpdateModuleFieldsInput!) {
    updateModule(input: { id: $id, fields: $fields }) {
      module {
        objectId
      }
    }
  }
`
  .replace(/\r?\n|\r/g, "")
  .replace(/\t/g, " ");

const UNCATEGORIZED = {
  objectId: "uncategorized",
  name: "Senza categoria",
  order: 9999,
  color: "#f2f5f6",
  darker_color: "#697982",
};

const MODEL_OPTIONS = [
  {
    value: "chatgpt-image-2",
    label: "ChatGPT Image 2",
    tag: "PNG",
  },
  {
    value: "nano-banana-2",
    label: "Nano Banana v2",
    tag: "PNG",
  },
  {
    value: "recraft-vector",
    label: "Recraft SVG",
    tag: "SVG",
  },
  {
    value: "recraft-vector-pro",
    label: "Recraft SVG Pro",
    tag: "SVG",
  },
];

// Shared Google Drive folder where finished module icons are collected.
const DRIVE_FOLDER_URL =
  "https://drive.google.com/drive/folders/1CKPQ-vtLV6FKKLO1a3SNZFc1oQTjP3Zj";

const AI_STEPS = [
  { key: "describe", title: "Descrivi", description: "Prompt" },
  { key: "generate", title: "Genera", description: "Modello e varianti" },
  { key: "choose", title: "Scegli", description: "Risultati" },
];

// Visible, editable system prompt sent to the prompt LLM. Mirrors the backend
// fallback (PROMPT_SYSTEM_INSTRUCTION in moduleIconGenerator.js).
const DEFAULT_AI_SYSTEM_PROMPT = `You write concise subject prompts for an AI text-to-image model that designs flat vector pictogram icons representing modules, features and workflows of an Italian funeral-sector management SaaS.

The software is used by funeral homes, service centers, crematoriums, suppliers and producers. Icons must feel professional, clean, sober, neutral and respectful. Avoid morbid, dark, dramatic, religious or literal death imagery unless the requested module clearly requires it.

Given a short idea, often written in Italian, convert it into ONE short English description of the subject glyph only.

Your goal is to choose one strong, concrete visual metaphor that can become a clear small app icon. Prefer practical business, administrative, operational, document, logistics, scheduling, communication, compliance, archive, workflow or inventory metaphors.

Output rules:

* Return only one sentence.
* Maximum length: about 40 words.
* Describe one unified pictogram, not a scene or a group of unrelated icons.
* Focus only on the concrete object/metaphor and its few key visual elements.
* Prefer simplified rounded geometric forms and strong silhouettes.
* Reduce complex objects to a few clean shapes.
* Avoid tiny details that would disappear at small icon size.
* Avoid generic symbols unless they clearly express the module.
* For abstract modules, choose a simple metaphor such as a ledger, folder, checklist, document, dashboard, calendar, tag, box, vehicle, map pin, user card, workflow node, shield, archive, drawer, stamp, handoff, or connected cards.
* Do not include text, letters, numbers, currency symbols, brand names, UI screens, people, skulls, coffins, crosses, candles, flowers, mourning imagery, or decorative elements unless specifically requested.
* Do not mention rendering details already handled elsewhere: background, canvas, tile, border, frame, shadow, perspective, stroke weight, color values, transparency, export format, gradients, fills or padding.

Return only the description sentence: no preamble, no quotes, no bullet points, no markdown.`;

// Prompts are built entirely on the frontend; the backend forwards them to fal
// as-is. buildLlmUserMessage -> /module-icons/prompt, buildImagePrompt ->
// /module-icons/generations.
const buildLlmUserMessage = ({ idea, categoryName }) =>
  [
    `Idea: ${idea}.`,
    categoryName ? `Module category: ${categoryName}.` : null,
    "Write the icon subject description.",
  ]
    .filter(Boolean)
    .join("\n");

const ICON_STYLE_OPTIONS = [
  { value: "outline", label: "Outline" },
  { value: "filled", label: "Pieno" },
  { value: "duotone", label: "Duotone" },
];

const STROKE_WEIGHT_OPTIONS = [
  { value: "thin", label: "Sottile" },
  { value: "regular", label: "Medio" },
  { value: "bold", label: "Spesso" },
];

const LOOK_OPTIONS = [
  { value: "minimal", label: "Minimale" },
  { value: "flat", label: "Flat" },
  { value: "realistic", label: "Realistico" },
];

const DETAIL_OPTIONS = [
  { value: "low", label: "Essenziale" },
  { value: "regular", label: "Medio" },
  { value: "high", label: "Ricco" },
];

const CORNER_OPTIONS = [
  { value: "rounded", label: "Arrotondati" },
  { value: "sharp", label: "Spigolosi" },
];

const PERSPECTIVE_OPTIONS = [
  { value: "flat", label: "Frontale" },
  { value: "isometric", label: "Isometrica" },
];

const STROKE_WORD = {
  thin: "thin",
  regular: "medium",
  bold: "bold",
};

// Tone of the opening line, driven by the "look" control.
const LOOK_OPENER = {
  minimal: "minimal flat vector",
  flat: "refined flat vector",
  realistic: "soft, semi-realistic vector",
};

// Adjective used inside the "Style:" sentence.
const LOOK_STYLE = {
  minimal: "ultra-minimalist",
  flat: "modern flat",
  realistic: "subtly realistic",
};

const DETAIL_PHRASES = {
  low: "Reduce every shape to its essentials; drop secondary details.",
  regular: null,
  high: "A few small secondary details are welcome where they genuinely aid recognition.",
};

const CORNER_PHRASES = {
  rounded: "soft rounded corners",
  sharp: "crisp, angular corners",
};

const PERSPECTIVE_PHRASES = {
  flat: "Front-facing 0° view, no perspective.",
  isometric: "Isometric 3/4 view with gentle, even depth.",
};

// Color + stroke sentence. Recraft (SVG/vector) takes its color from the native
// `colors` parameter, so that prompt stays color-agnostic; raster models
// (GPT image, Nano Banana) must read the color from the words.
const buildRenderingPhrase = ({
  iconStyle,
  strokeWeight,
  isVector,
  foregroundColor,
  foregroundColorLight,
}) => {
  const stroke = STROKE_WORD[strokeWeight] || STROKE_WORD.regular;
  const mainColor = !isVector && foregroundColor ? foregroundColor : null;
  const fillColor =
    !isVector && foregroundColorLight ? foregroundColorLight : null;

  if (iconStyle === "filled") {
    const colorPart = mainColor ? ` in ${mainColor}` : " in a single color";
    return `Render the symbol as one clean, solid filled silhouette${colorPart}, with no outline. One flat color only, no gradients, no shading.`;
  }

  if (iconStyle === "duotone") {
    if (mainColor) {
      return `Use ${mainColor} for the ${stroke}-weight outline strokes (rounded caps and joins) and ${
        fillColor || "a lighter shade of the same color"
      } for soft, flat fills. Only these two tones, no gradients, no shading.`;
    }
    return `Two-tone line art: ${stroke}-weight outline strokes with rounded caps and joins, plus soft, flat fills in a lighter shade of the same color. No gradients, no shading.`;
  }

  return `${
    mainColor
      ? `Use ${mainColor} as a single-color stroke`
      : "Use a single-color stroke"
  }, ${stroke} weight, with rounded caps and joins. Optional tiny filled accents in the same color are allowed only to improve readability.`;
};

// Premium, model-friendly prompt built entirely from the UI controls, shaped
// like a clean reference-icon brief. No internal OFC/Mission Control jargon a
// text-to-image model can't know.
const buildImagePrompt = ({
  subject,
  foregroundColor,
  foregroundColorLight,
  isVector,
  iconStyle,
  strokeWeight,
  look,
  detail,
  corners,
  perspective,
}) => {
  const styleLine = [
    `Style: ${
      LOOK_STYLE[look] || LOOK_STYLE.flat
    } premium SaaS icon, Apple-like, clean, calm and professional.`,
    PERSPECTIVE_PHRASES[perspective] || PERSPECTIVE_PHRASES.flat,
    `Balanced geometry, ${
      CORNER_PHRASES[corners] || CORNER_PHRASES.rounded
    }, strong silhouette.`,
    DETAIL_PHRASES[detail],
  ]
    .filter(Boolean)
    .join(" ");

  const renderingLine = `${buildRenderingPhrase({
    iconStyle,
    strokeWeight,
    isVector,
    foregroundColor,
    foregroundColorLight,
  })} The icon must stay crisp and recognizable at 32px.`;

  return [
    `A ${
      LOOK_OPENER[look] || LOOK_OPENER.flat
    } icon for a module of OFC, a funeral-industry SaaS ERP for funeral homes.`,
    `Create a single, unified symbol: ${subject || "the module concept"}.`,
    styleLine,
    renderingLine,
    "Transparent background. No background tile, no border, no shadow, no gradient, no text, no numbers, no letters, no extra decorative elements.",
  ].join("\n\n");
};

const STATUS_LABELS = {
  SUBMITTED: "Inviato",
  IN_QUEUE: "In coda",
  IN_PROGRESS: "In generazione",
  STILL_RUNNING: "Ancora in corso",
  COMPLETED: "Pronto",
  FAILED: "Errore",
  ERROR: "Errore",
  UNKNOWN: "In corso",
};

const BUILDER_STEPS = [
  { key: "category", title: "Categoria", description: "Tema colore" },
  { key: "source", title: "Genera icona", description: "AI, SVG o URL" },
  { key: "refine", title: "Rifinitura", description: "Posizione e colori" },
];

// Brand icon corner radius: fixed at rx=20 on a 100 viewBox (see automezzi.svg).
const ICON_RADIUS_PERCENT = 20;

const DEFAULT_BUILDER = {
  categoryId: "",
  size: 512,
  // Icon footprint as a percentage of the canvas. 50% matches the previous
  // 25% padding default; the slider now goes past 100% so the glyph can fill
  // or overflow the background square.
  scalePercent: 50,
  // Icon position offset from center, as a percentage of the canvas.
  offsetXPercent: 0,
  offsetYPercent: 0,
  backgroundColor: "#eef7f7",
  foregroundColor: "#195c7e",
  // Prompt style controls (shape the generated image prompt).
  iconStyle: "outline",
  strokeWeight: "regular",
  look: "minimal",
  detail: "regular",
  corners: "rounded",
  perspective: "flat",
  foregroundColorLight: "#bcdfe2",
};

const ICON_SCALE_MIN = 10;
const ICON_SCALE_MAX = 160;
const ICON_OFFSET_RANGE = 60;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const AI_HISTORY_STORAGE_KEY = "ofc.moduleIcons.aiGenerations.v1";
const MAX_AI_HISTORY_ITEMS = 24;
const MAX_STORED_STATUS_RESPONSES = 24;

const MODEL_TIMING_CONFIGS = {
  "chatgpt-image-2": {
    expectedSeconds: 240,
    maxPollingSeconds: 600,
    pollIntervalMs: 5000,
    note: "Log prod 29/05: ancora in corso dopo 3m 25s",
  },
  "nano-banana-2": {
    expectedSeconds: 120,
    maxPollingSeconds: 420,
    pollIntervalMs: 4000,
    note: "Stima prudente per PNG AI",
  },
  "recraft-vector": {
    expectedSeconds: 45,
    maxPollingSeconds: 240,
    pollIntervalMs: 3000,
    note: "Run prod breve osservato: circa 39s",
  },
  "recraft-vector-pro": {
    expectedSeconds: 60,
    maxPollingSeconds: 300,
    pollIntervalMs: 3000,
    note: "Recraft Pro: resa migliore, un filo più lento",
  },
};

const DEFAULT_MODEL_TIMING = {
  expectedSeconds: 120,
  maxPollingSeconds: 420,
  pollIntervalMs: 4000,
  note: "Stima standard",
};

const getModelTiming = (value) =>
  MODEL_TIMING_CONFIGS[value] || DEFAULT_MODEL_TIMING;

const isGenerationErrorStatus = (status) =>
  status === "FAILED" || status === "ERROR";

const isGenerationActiveStatus = (status) =>
  ["SUBMITTED", "IN_QUEUE", "IN_PROGRESS", "STILL_RUNNING", "UNKNOWN"].includes(
    status,
  );

const normalizeHex = (value, fallback) =>
  typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value.trim())
    ? value.trim()
    : fallback;

// Lighten a hex color toward white (used to derive a duotone fill tint).
const lightenHex = (hex, ratio = 0.6) => {
  const match = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());

  if (!match) {
    return hex;
  }

  const value = parseInt(match[1], 16);
  const mix = (channel) => Math.round(channel + (255 - channel) * ratio);
  const r = mix((value >> 16) & 255);
  const g = mix((value >> 8) & 255);
  const b = mix(value & 255);

  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

const createHistoryId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const readAiHistory = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }

  try {
    const stored = JSON.parse(
      window.localStorage.getItem(AI_HISTORY_STORAGE_KEY) || "[]",
    );

    return Array.isArray(stored) ? stored.slice(0, MAX_AI_HISTORY_ITEMS) : [];
  } catch (error) {
    console.warn("Could not read icon AI history:", error);
    return [];
  }
};

const writeAiHistory = (history) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(
      AI_HISTORY_STORAGE_KEY,
      JSON.stringify(history.slice(0, MAX_AI_HISTORY_ITEMS)),
    );
  } catch (error) {
    console.warn("Could not persist icon AI history:", error);
  }
};

const formatHistoryDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString("it-IT", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const formatDuration = (seconds) => {
  const normalized = Math.max(0, Math.round(seconds || 0));
  const minutes = Math.floor(normalized / 60);
  const remainingSeconds = normalized % 60;

  if (minutes === 0) {
    return `${remainingSeconds}s`;
  }

  return remainingSeconds > 0
    ? `${minutes}m ${remainingSeconds}s`
    : `${minutes}m`;
};

const getTimestampMs = (value) => {
  if (!value) {
    return null;
  }

  const timestamp = new Date(value).getTime();

  return Number.isNaN(timestamp) ? null : timestamp;
};

const getGenerationElapsedSeconds = (record, nowMs) => {
  const startedAtMs = getTimestampMs(record?.startedAt || record?.createdAt);

  if (!startedAtMs) {
    return 0;
  }

  const finishedAtMs =
    getTimestampMs(record?.completedAt) ||
    getTimestampMs(record?.failedAt) ||
    (isGenerationActiveStatus(record?.status) ? nowMs : null) ||
    getTimestampMs(record?.updatedAt) ||
    nowMs;

  return Math.max(0, (finishedAtMs - startedAtMs) / 1000);
};

const getGenerationProgressPercent = (record, nowMs) => {
  if (record?.status === "COMPLETED") {
    return 100;
  }

  const timing = getModelTiming(record?.model);
  const elapsedSeconds = getGenerationElapsedSeconds(record, nowMs);
  const estimatedPercent = (elapsedSeconds / timing.expectedSeconds) * 90;
  const statusFloor =
    record?.status === "IN_PROGRESS"
      ? 32
      : record?.status === "IN_QUEUE"
        ? 8
        : record?.status === "STILL_RUNNING"
          ? 92
          : 3;
  const cap = isGenerationErrorStatus(record?.status)
    ? 100
    : record?.status === "STILL_RUNNING"
      ? 97
      : 96;

  return clamp(Math.round(Math.max(statusFloor, estimatedPercent)), 0, cap);
};

const getProgressStatus = (status) => {
  if (status === "COMPLETED") {
    return "success";
  }

  if (isGenerationErrorStatus(status)) {
    return "exception";
  }

  return "active";
};

const safeJson = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return JSON.stringify({ error: "Payload non serializzabile" }, null, 2);
  }
};

// fal.ai (and our proxy) can return an error as an object/array (e.g. a 422
// "detail" list of { loc, msg, type }). new Error(object) renders as
// "[object Object]" and hides the cause, so flatten it into a readable string.
const formatApiError = (error, fallback) => {
  if (typeof error === "string" && error.trim()) {
    return error;
  }

  if (Array.isArray(error)) {
    const messages = error
      .map((item) =>
        typeof item === "string" ? item : item?.msg || item?.message || null,
      )
      .filter(Boolean);

    if (messages.length) {
      return messages.join("; ");
    }
  }

  if (error && typeof error === "object") {
    if (typeof error.msg === "string") {
      return error.msg;
    }

    if (typeof error.message === "string") {
      return error.message;
    }

    if (typeof error.detail === "string") {
      return error.detail;
    }

    try {
      return JSON.stringify(error);
    } catch (serializeError) {
      return fallback;
    }
  }

  return fallback;
};

const getModelOption = (value) =>
  MODEL_OPTIONS.find((option) => option.value === value);

const getModelLabel = (value) => getModelOption(value)?.label || value;

const isPngAiModel = (value) => getModelOption(value)?.tag === "PNG";

const getAssetFetchKey = (url, removeBackground) =>
  `${url || ""}::${removeBackground ? "pixian" : "raw"}`;

const buildAssetProxyPath = (url, removeBackground) => {
  const params = new URLSearchParams({ url });

  if (removeBackground) {
    params.set("removeBackground", "true");
  }

  return `/module-icons/assets?${params.toString()}`;
};

const sanitizeFilename = (value) => {
  const normalized = (value || "module-icon")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || "module-icon";
};

const escapeXml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const svgToDataUri = (svg) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

const downloadBlob = (filename, blob) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadText = (filename, text, contentType) => {
  downloadBlob(filename, new Blob([text], { type: contentType }));
};

const directDownload = (filename, url) => {
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const getIconExtension = (url, contentType) => {
  if (contentType?.includes("svg")) {
    return "svg";
  }

  if (contentType?.includes("png")) {
    return "png";
  }

  if (contentType?.includes("jpeg") || contentType?.includes("jpg")) {
    return "jpg";
  }

  if (contentType?.includes("webp")) {
    return "webp";
  }

  try {
    const pathname = new URL(url).pathname;
    const extension = pathname.match(/\.([a-z0-9]{2,5})$/i)?.[1];

    if (extension) {
      return extension.toLowerCase();
    }
  } catch {
    // URL parsing is best effort; default below keeps the action usable.
  }

  return "png";
};

const getModuleIconFilename = (module, contentType) => {
  const baseName = sanitizeFilename(
    (module.icon?.name || module.name || "module-icon").replace(
      /\.[a-z0-9]{2,5}$/i,
      "",
    ),
  );
  const extension = getIconExtension(module.iconUrl, contentType);

  return `${baseName}.${extension}`;
};

const copyTextToClipboard = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const input = document.createElement("textarea");

  input.value = value;
  input.setAttribute("readonly", "");
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  document.body.removeChild(input);
};

const renderSvgToPngBlob = (svg, size) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const svgUrl = URL.createObjectURL(
      new Blob([svg], { type: "image/svg+xml;charset=utf-8" }),
    );

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      canvas.width = size;
      canvas.height = size;
      context.drawImage(image, 0, 0, size, size);
      URL.revokeObjectURL(svgUrl);

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error("PNG export failed"));
      }, "image/png");
    };

    image.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      reject(new Error("SVG preview could not be rendered as PNG"));
    };

    image.src = svgUrl;
  });

const getCategoryTone = (category) => ({
  backgroundColor: normalizeHex(
    category?.color,
    DEFAULT_BUILDER.backgroundColor,
  ),
  foregroundColor: normalizeHex(
    category?.darker_color,
    DEFAULT_BUILDER.foregroundColor,
  ),
});

// Recolor a mono/duotone SVG glyph to a single accent color: rewrite explicit
// fill/stroke colors (leaving none/transparent/currentColor/gradients intact)
// so the "Icona" color actually takes effect on SVG sources. Raster (PNG)
// glyphs are drawn via <image> and cannot be recolored this way.
const recolorSvg = (svg, color) =>
  svg
    .replace(
      /(fill|stroke)=("|')(?!none|transparent|currentColor|url\()[^"']*\2/gi,
      (_match, attr, quote) => `${attr}=${quote}${color}${quote}`,
    )
    .replace(
      /(fill|stroke)\s*:\s*(?!none|transparent|currentColor|url\()#[0-9a-fA-F]{3,8}/gi,
      (_match, attr) => `${attr}:${color}`,
    );

const buildNestedSvg = (svgText, x, y, iconSize, color) => {
  if (!svgText) {
    return null;
  }

  let trimmed = svgText
    .replace(/<\?xml[\s\S]*?\?>/gi, "")
    .replace(/<!doctype[\s\S]*?>/gi, "")
    .trim();

  if (!/^<svg[\s>]/i.test(trimmed)) {
    return null;
  }

  if (color) {
    trimmed = recolorSvg(trimmed, color);
  }

  return trimmed.replace(/^<svg\b([^>]*)>/i, (_, attrs = "") => {
    const cleanedAttrs = attrs.replace(
      /\s(width|height|x|y|preserveAspectRatio|fill)=("[^"]*"|'[^']*')/gi,
      "",
    );
    const fillAttr = color ? ` fill="${color}"` : "";

    return `<svg${cleanedAttrs}${fillAttr} x="${x}" y="${y}" width="${iconSize}" height="${iconSize}" preserveAspectRatio="xMidYMid meet">`;
  });
};

// Alignment grid drawn on top of the preview tile only: a light 8x8 grid plus
// a stronger centre cross to eyeball centring while moving/scaling the glyph.
// Stroke widths scale with the viewBox so they stay ~1px once the SVG is shrunk
// to the preview size. Never included in exports.
const buildGridOverlay = (size) => {
  const divisions = 8;
  const step = size / divisions;
  const fineStroke = Math.max(1, Math.round((size / 256) * 10) / 10);
  const axisStroke = Math.max(1.5, Math.round((size / 150) * 10) / 10);
  const center = Math.round(size / 2);
  const lines = [];

  for (let i = 1; i < divisions; i += 1) {
    const pos = Math.round(step * i);
    lines.push(
      `<line x1="${pos}" y1="0" x2="${pos}" y2="${size}" />` +
        `<line x1="0" y1="${pos}" x2="${size}" y2="${pos}" />`,
    );
  }

  return `
  <g fill="none" stroke-linecap="square" pointer-events="none">
    <g stroke="#18272f" stroke-width="${fineStroke}" opacity="0.12">
      ${lines.join("")}
    </g>
    <g stroke="#ec664e" stroke-width="${axisStroke}" opacity="0.55">
      <line x1="${center}" y1="0" x2="${center}" y2="${size}" />
      <line x1="0" y1="${center}" x2="${size}" y2="${center}" />
    </g>
  </g>`;
};

const buildModuleIconSvg = ({
  asset,
  builder,
  includeBackground,
  gridOverlay = false,
}) => {
  const size = builder.size;
  const radius = Math.round(size * (ICON_RADIUS_PERCENT / 100));
  const scalePercent = clamp(
    builder.scalePercent ?? 50,
    ICON_SCALE_MIN,
    ICON_SCALE_MAX,
  );
  const iconSize = Math.max(1, Math.round(size * (scalePercent / 100)));
  const offsetX = Math.round(size * ((builder.offsetXPercent ?? 0) / 100));
  const offsetY = Math.round(size * ((builder.offsetYPercent ?? 0) / 100));
  const x = Math.round((size - iconSize) / 2) + offsetX;
  const y = Math.round((size - iconSize) / 2) + offsetY;
  const assetHref = asset?.href;
  const nestedSvg = buildNestedSvg(
    asset?.svgText,
    x,
    y,
    iconSize,
    builder.foregroundColor,
  );
  const iconMarkup =
    nestedSvg ||
    (assetHref
      ? `<image href="${escapeXml(
          assetHref,
        )}" x="${x}" y="${y}" width="${iconSize}" height="${iconSize}" preserveAspectRatio="xMidYMid meet" />`
      : "");

  // With a background, clip the icon to the rounded square so an oversized or
  // shifted glyph fills the tile cleanly instead of bleeding past the corners.
  // The glyph-only export stays unclipped so it can be used full-bleed.
  if (includeBackground) {
    return `
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  <defs>
    <clipPath id="icon-tile-clip">
      <rect width="${size}" height="${size}" rx="${radius}" />
    </clipPath>
  </defs>
  <g clip-path="url(#icon-tile-clip)">
    <rect width="${size}" height="${size}" fill="${escapeXml(
      builder.backgroundColor,
    )}" />
    ${iconMarkup}
    ${gridOverlay ? buildGridOverlay(size) : ""}
  </g>
</svg>`.trim();
  }

  return `
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  ${iconMarkup}
</svg>`.trim();
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const Icons = () => {
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const apiBaseUrl = config.baseUrl[env];
  const graphqlUrl = `${apiBaseUrl}/graphql`;
  const { getTokenSilently } = useAuth0();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [iconStatusFilter, setIconStatusFilter] = useState("all");
  const [builder, setBuilder] = useState(DEFAULT_BUILDER);
  const [builderStep, setBuilderStep] = useState(0);
  const [showAlignmentGrid, setShowAlignmentGrid] = useState(true);
  const [builderModalOpen, setBuilderModalOpen] = useState(false);
  const [assetMode, setAssetMode] = useState(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiHistory, setAiHistory] = useState(readAiHistory);
  const [model, setModel] = useState("recraft-vector");
  // Pre-selected for PNG models (gpt-image-2, nano-banana-2); ignored/disabled
  // for Recraft SVG, which is already transparent.
  const [removeAiPngBackground, setRemoveAiPngBackground] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [previousPrompt, setPreviousPrompt] = useState(null);
  // null = derive the final image prompt from the subject; a string = the user
  // edited it manually (used verbatim at submit).
  const [imagePromptOverride, setImagePromptOverride] = useState(null);
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_AI_SYSTEM_PROMPT);
  const [promptLoading, setPromptLoading] = useState(false);
  const [variantCount, setVariantCount] = useState(3);
  const [aiStep, setAiStep] = useState(0);
  const [svgInput, setSvgInput] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [asset, setAsset] = useState(null);
  const [aiPreviewAssets, setAiPreviewAssets] = useState({});
  const requestedThumbUrlsRef = useRef(new Set());
  const initialQueryAppliedRef = useRef(false);
  const [choosingImageKey, setChoosingImageKey] = useState(null);
  const [generation, setGeneration] = useState({
    loading: false,
    status: null,
    requestId: null,
    historyId: null,
    model: null,
    startedAt: null,
  });
  const [checkingGenerationId, setCheckingGenerationId] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [exporting, setExporting] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applyTargetId, setApplyTargetId] = useState(null);
  const [linkedModuleId, setLinkedModuleId] = useState(null);
  const [applying, setApplying] = useState(false);

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getTokenSilently();
      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query: MODULE_ICONS_QUERY,
          variables: {
            where: {},
          },
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const modulesData = response.data
        .map((node) => ({
          ...node,
          key: node.objectId,
          iconUrl: node.icon?.url,
          module_category_pointer:
            node.module_category_pointer || UNCATEGORIZED,
        }))
        .sort(compareByOrderAndName);

      setModules(modulesData);
    } catch (error) {
      console.error("Error fetching module icons:", error);
      message.error("Errore nel caricamento delle icone modulo");
    } finally {
      setLoading(false);
    }
  }, [getTokenSilently, graphqlUrl]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  useEffect(() => {
    writeAiHistory(aiHistory);
  }, [aiHistory]);

  const hasPendingAiRun = useMemo(
    () =>
      generation.loading ||
      aiHistory.some((record) =>
        ["SUBMITTED", "IN_QUEUE", "IN_PROGRESS", "UNKNOWN"].includes(
          record.status,
        ),
      ),
    [aiHistory, generation.loading],
  );

  useEffect(() => {
    if (!hasPendingAiRun) {
      return undefined;
    }

    setNowMs(Date.now());
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, [hasPendingAiRun]);

  const addAiHistoryRecord = useCallback((record) => {
    setAiHistory((current) =>
      [record, ...current.filter((item) => item.id !== record.id)].slice(
        0,
        MAX_AI_HISTORY_ITEMS,
      ),
    );
  }, []);

  const patchAiHistoryRecord = useCallback((recordId, patch) => {
    setAiHistory((current) =>
      current.map((item) => {
        if (item.id !== recordId) {
          return item;
        }

        const nextPatch = typeof patch === "function" ? patch(item) : patch;

        return {
          ...item,
          ...nextPatch,
          updatedAt: new Date().toISOString(),
        };
      }),
    );
  }, []);

  const categories = useMemo(() => {
    const byId = new Map();

    modules.forEach((module) => {
      const category = getModuleCategory(module) || UNCATEGORIZED;
      const key = category.objectId || UNCATEGORIZED.objectId;

      if (!byId.has(key)) {
        byId.set(key, {
          ...category,
          objectId: key,
          moduleCount: 0,
          iconCount: 0,
        });
      }

      const current = byId.get(key);
      current.moduleCount += 1;
      current.iconCount += module.iconUrl ? 1 : 0;
    });

    return Array.from(byId.values()).sort((left, right) => {
      const orderDiff =
        Number(left.order || 9999) - Number(right.order || 9999);

      if (orderDiff !== 0) {
        return orderDiff;
      }

      return (left.name || "").localeCompare(right.name || "", "it");
    });
  }, [modules]);

  useEffect(() => {
    if (builder.categoryId || categories.length === 0) {
      return;
    }

    const firstCategory = categories[0];
    const tone = getCategoryTone(firstCategory);

    setBuilder((current) => ({
      ...current,
      categoryId: firstCategory.objectId,
      backgroundColor: tone.backgroundColor,
      foregroundColor: tone.foregroundColor,
    }));
  }, [builder.categoryId, categories]);

  // Deep link from the module editor: ?category=<id>&builder=1 preselects the
  // category theme and opens the builder. Applied once, after categories load.
  useEffect(() => {
    if (initialQueryAppliedRef.current || categories.length === 0) {
      return;
    }

    initialQueryAppliedRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const categoryParam = params.get("category");
    const moduleParam = params.get("module");
    const match = categoryParam
      ? categories.find((category) => category.objectId === categoryParam)
      : null;

    if (moduleParam) {
      setLinkedModuleId(moduleParam);
      setApplyTargetId(moduleParam);
    }

    if (match) {
      const tone = getCategoryTone(match);
      setBuilder((current) => ({
        ...current,
        categoryId: match.objectId,
        backgroundColor: tone.backgroundColor,
        foregroundColor: tone.foregroundColor,
      }));
    }

    if (params.get("builder") === "1") {
      setBuilderStep(0);
      setBuilderModalOpen(true);
    }
  }, [categories]);

  const selectedCategory = useMemo(
    () =>
      categories.find((category) => category.objectId === builder.categoryId) ||
      categories[0] ||
      UNCATEGORIZED,
    [builder.categoryId, categories],
  );

  const filteredModules = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();

    return modules.filter((module) => {
      if (
        categoryFilter !== "all" &&
        getModuleCategory(module).objectId !== categoryFilter
      ) {
        return false;
      }

      if (iconStatusFilter === "with" && !module.iconUrl) {
        return false;
      }

      if (iconStatusFilter === "missing" && module.iconUrl) {
        return false;
      }

      if (!searchTerm) {
        return true;
      }

      return [module.name, getModuleCategory(module).name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(searchTerm);
    });
  }, [modules, search, categoryFilter, iconStatusFilter]);

  const groupedModules = useMemo(
    () =>
      categories
        .map((category) => ({
          category,
          modules: filteredModules.filter(
            (module) =>
              getModuleCategory(module).objectId === category.objectId,
          ),
        }))
        .filter((group) => group.modules.length > 0),
    [categories, filteredModules],
  );

  const fullPreviewSvg = useMemo(
    () =>
      buildModuleIconSvg({
        asset,
        builder,
        includeBackground: true,
        gridOverlay: showAlignmentGrid,
      }),
    [asset, builder, showAlignmentGrid],
  );
  const glyphPreviewSvg = useMemo(
    () => buildModuleIconSvg({ asset, builder, includeBackground: false }),
    [asset, builder],
  );
  // Only SVG glyphs can be recolored; raster (PNG/JPG) assets keep their pixels.
  const isRasterAsset = Boolean(asset && !asset.svgText);
  // Standard icon color of the builder's category, plus the category whose
  // standard matches the current color (so the preset Select can reflect it).
  const categoryStandardForeground =
    getCategoryTone(selectedCategory).foregroundColor;
  const matchingStandardCategoryId = categories.find(
    (category) =>
      getCategoryTone(category).foregroundColor.toLowerCase() ===
      (builder.foregroundColor || "").toLowerCase(),
  )?.objectId;

  const updateBuilder = (patch) => {
    setBuilder((current) => ({
      ...current,
      ...patch,
    }));
  };

  // Changing the subject (the "Descrivi l'icona" text) invalidates any
  // hand-edited final prompt: that override was derived from the previous
  // subject, so drop it and let the image prompt auto-derive again.
  const changeSubjectPrompt = (value) => {
    setPrompt(value);
    setImagePromptOverride(null);
  };

  // Start a fresh icon: clear source/prompt and reset geometry, keeping the
  // current category theme. Past AI generations stay in localStorage history.
  const resetBuilder = () => {
    setAsset(null);
    setAssetMode(null);
    setPrompt("");
    setPreviousPrompt(null);
    setImagePromptOverride(null);
    setBuilderStep(0);
    setBuilder((current) => ({
      ...DEFAULT_BUILDER,
      categoryId: current.categoryId,
      backgroundColor: current.backgroundColor,
      foregroundColor: current.foregroundColor,
    }));
  };

  // Open the builder already linked to a module, themed with its category.
  // Same flow as the module-editor deep link (?module=&category=&builder=1):
  // used by the per-card "Modifica" / "Crea icona" actions.
  const openBuilderForModule = (module) => {
    const category = getModuleCategory(module);
    const tone = getCategoryTone(category);
    setLinkedModuleId(module.objectId);
    setApplyTargetId(module.objectId);
    setAsset(null);
    setAssetMode(null);
    setPrompt("");
    setPreviousPrompt(null);
    setImagePromptOverride(null);
    setBuilderStep(0);
    setBuilder({
      ...DEFAULT_BUILDER,
      categoryId: category.objectId,
      backgroundColor: tone.backgroundColor,
      foregroundColor: tone.foregroundColor,
    });
    setBuilderModalOpen(true);
  };

  const applyCategory = (categoryId) => {
    const category =
      categories.find((item) => item.objectId === categoryId) || UNCATEGORIZED;
    const tone = getCategoryTone(category);

    updateBuilder({
      categoryId,
      backgroundColor: tone.backgroundColor,
      foregroundColor: tone.foregroundColor,
    });
  };

  const apiFetch = useCallback(
    async (path, options = {}) => {
      const token = await getTokenSilently();
      const response = await fetch(`${apiBaseUrl}${path}`, {
        ...options,
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(options.headers || {}),
        },
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          formatApiError(payload.error, `HTTP ${response.status}`),
        );
      }

      return payload;
    },
    [apiBaseUrl, getTokenSilently],
  );

  useEffect(() => {
    if (!aiModalOpen || aiHistory.length === 0) {
      return;
    }

    const assetTargets = Array.from(
      new Map(
        aiHistory.flatMap((record) => {
          const removeBackground = Boolean(
            record.removeBackground && isPngAiModel(record.model),
          );

          return (record.images || record.resultResponse?.data?.images || [])
            .map((image) =>
              image?.url
                ? [
                    getAssetFetchKey(image.url, removeBackground),
                    {
                      url: image.url,
                      removeBackground,
                    },
                  ]
                : null,
            )
            .filter(Boolean);
        }),
      ).values(),
    );

    assetTargets.slice(0, 16).forEach(async ({ url, removeBackground }) => {
      const assetKey = getAssetFetchKey(url, removeBackground);

      // Guard against duplicate fetches via a ref: deciding inside the
      // setAiPreviewAssets updater was unreliable because React does not
      // run functional updaters synchronously, so the fetch was often
      // skipped and the thumbnail stayed stuck on its spinner.
      if (requestedThumbUrlsRef.current.has(assetKey)) {
        return;
      }

      requestedThumbUrlsRef.current.add(assetKey);
      setAiPreviewAssets((current) =>
        current[assetKey]?.src || current[assetKey]?.error
          ? current
          : { ...current, [assetKey]: { loading: true } },
      );

      try {
        const payload = await apiFetch(
          buildAssetProxyPath(url, removeBackground),
          {
            method: "GET",
          },
        );
        const assetData = payload.data;

        setAiPreviewAssets((current) => ({
          ...current,
          [assetKey]: assetData?.dataUri
            ? {
                loading: false,
                src: assetData.dataUri,
                contentType: assetData.contentType,
              }
            : { loading: false, error: true },
        }));
      } catch (error) {
        console.warn("Could not load AI thumbnail:", error);
        // Allow a later effect run to retry this URL.
        requestedThumbUrlsRef.current.delete(assetKey);
        setAiPreviewAssets((current) => ({
          ...current,
          [assetKey]: {
            loading: false,
            error: true,
          },
        }));
      }
    });
  }, [aiHistory, aiModalOpen, apiFetch]);

  const importGeneratedAsset = async (image, removeBackground = false) => {
    const payload = await apiFetch(
      buildAssetProxyPath(image.url, removeBackground),
      {
        method: "GET",
      },
    );
    const assetData = payload.data;
    const nextAsset = {
      href: assetData.dataUri,
      sourceUrl: image.url,
      contentType: assetData.contentType,
      text: assetData.text,
      svgText: assetData.text,
      fileName: image.fileName || "generated-icon",
      label: image.fileName || "Icona generata",
    };

    setAsset(nextAsset);

    return {
      asset: nextAsset,
      assetData,
      payload,
    };
  };

  const appendGenerationStatusResponse = (
    historyId,
    status,
    statusPayload,
    checkedAt,
  ) => {
    patchAiHistoryRecord(historyId, (current) => ({
      status,
      lastCheckedAt: checkedAt,
      statusResponses: [
        ...(current.statusResponses || []),
        {
          at: checkedAt,
          response: statusPayload,
        },
      ].slice(-MAX_STORED_STATUS_RESPONSES),
    }));
  };

  const completeGenerationRecord = async ({ historyId, recordModel, requestId }) => {
    const resultPayload = await apiFetch(
      `/module-icons/generations/${recordModel}/${requestId}/result`,
      {
        method: "GET",
      },
    );
    const images = resultPayload.data?.images || [];

    if (!images.some((image) => image?.url)) {
      throw new Error("Nessuna immagine restituita da fal.ai");
    }

    patchAiHistoryRecord(historyId, {
      status: "COMPLETED",
      completedAt: new Date().toISOString(),
      resultResponse: resultPayload,
      images,
      error: null,
    });

    return {
      resultPayload,
      images,
    };
  };

  const refreshGenerationRecord = async (record) => {
    if (!record?.requestId || !record?.model) {
      message.warning("RequestId mancante per questa generazione");
      return;
    }

    try {
      setCheckingGenerationId(record.id);
      const statusPayload = await apiFetch(
        `/module-icons/generations/${record.model}/${record.requestId}/status`,
        {
          method: "GET",
        },
      );
      const status = statusPayload.data?.status || "UNKNOWN";
      const checkedAt = new Date().toISOString();

      appendGenerationStatusResponse(
        record.id,
        status,
        statusPayload,
        checkedAt,
      );
      setGeneration((current) =>
        current.historyId === record.id
          ? {
              ...current,
              loading: false,
              status,
              requestId: record.requestId,
              model: record.model,
              startedAt: record.startedAt || record.createdAt,
            }
          : current,
      );

      if (status === "COMPLETED") {
        await completeGenerationRecord({
          historyId: record.id,
          recordModel: record.model,
          requestId: record.requestId,
        });
        setGeneration((current) =>
          current.historyId === record.id
            ? {
                ...current,
                loading: false,
                status: "COMPLETED",
              }
            : current,
        );
        message.success("Generazione completata: scegli una risposta");
        return;
      }

      if (isGenerationErrorStatus(status)) {
        patchAiHistoryRecord(record.id, {
          status,
          failedAt: checkedAt,
          error: "Generazione fal.ai fallita",
        });
        message.error("Generazione fal.ai fallita");
        return;
      }

      message.info("Generazione ancora in corso su fal.ai");
    } catch (error) {
      console.error("Error refreshing module icon generation:", error);
      message.error(error.message || "Errore durante il controllo risultato");
    } finally {
      setCheckingGenerationId(null);
    }
  };

  const improvePrompt = async () => {
    const ideaValue = prompt.trim();

    if (!ideaValue) {
      message.warning("Scrivi prima un'idea o un prompt da migliorare");
      return;
    }

    try {
      setPromptLoading(true);
      const payload = await apiFetch("/module-icons/prompt", {
        method: "POST",
        body: JSON.stringify({
          prompt: buildLlmUserMessage({
            idea: ideaValue,
            categoryName: selectedCategory?.name,
          }),
          systemPrompt,
        }),
      });
      const suggested = payload.data?.suggestedPrompt;

      if (!suggested) {
        throw new Error("Nessun prompt restituito dal modello");
      }

      setPreviousPrompt(ideaValue);
      changeSubjectPrompt(suggested);
      message.success("Prompt migliorato: rivedilo o genera l'icona");
    } catch (error) {
      console.error("Error improving prompt:", error);
      message.error(
        error.message || "Errore durante il miglioramento del prompt",
      );
    } finally {
      setPromptLoading(false);
    }
  };

  const generateAsset = async () => {
    const promptValue = prompt.trim();
    const finalPrompt = (
      imagePromptOverride != null
        ? imagePromptOverride
        : buildImagePrompt({
            subject: promptValue,
            foregroundColor: builder.foregroundColor,
            foregroundColorLight: builder.foregroundColorLight,
            isVector: getModelOption(model)?.tag === "SVG",
            iconStyle: builder.iconStyle,
            strokeWeight: builder.strokeWeight,
            look: builder.look,
            detail: builder.detail,
            corners: builder.corners,
            perspective: builder.perspective,
          })
    ).trim();

    if (!finalPrompt) {
      message.warning("Inserisci o genera un prompt per creare l'icona");
      return;
    }

    const historyId = createHistoryId();
    const removeBackground = removeAiPngBackground && isPngAiModel(model);
    const modelSupportsVariants = isPngAiModel(model);
    const numVariants = modelSupportsVariants ? variantCount : 1;
    const timing = getModelTiming(model);
    const requestBody = {
      model,
      prompt: finalPrompt,
      foregroundColor: builder.foregroundColor,
      secondaryColor:
        builder.iconStyle === "duotone"
          ? builder.foregroundColorLight
          : undefined,
      removeBackground,
      numVariants,
    };
    const createdAt = new Date().toISOString();

    addAiHistoryRecord({
      id: historyId,
      createdAt,
      updatedAt: createdAt,
      status: "SUBMITTED",
      model,
      modelLabel: getModelLabel(model),
      prompt: promptValue,
      categoryName: selectedCategory?.name,
      foregroundColor: builder.foregroundColor,
      backgroundColor: builder.backgroundColor,
      removeBackground,
      numVariants,
      startedAt: createdAt,
      timing: {
        estimatedSeconds: timing.expectedSeconds,
        maxPollingSeconds: timing.maxPollingSeconds,
        pollIntervalMs: timing.pollIntervalMs,
      },
      request: {
        method: "POST",
        path: "/module-icons/generations",
        body: requestBody,
      },
      submitResponse: null,
      statusResponses: [],
      resultResponse: null,
      images: [],
    });

    try {
      setGeneration({
        loading: true,
        status: "SUBMITTED",
        requestId: null,
        historyId,
        model,
        startedAt: createdAt,
      });

      const submitPayload = await apiFetch("/module-icons/generations", {
        method: "POST",
        body: JSON.stringify(requestBody),
      });
      const requestId = submitPayload.data?.requestId;

      if (!requestId) {
        throw new Error("fal.ai non ha restituito un requestId");
      }

      patchAiHistoryRecord(historyId, {
        requestId,
        status: "IN_QUEUE",
        submitResponse: submitPayload,
      });
      setGeneration({
        loading: true,
        status: "IN_QUEUE",
        requestId,
        historyId,
        model,
        startedAt: createdAt,
      });

      let completed = false;
      const maxAttempts = Math.ceil(
        (timing.maxPollingSeconds * 1000) / timing.pollIntervalMs,
      );

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await wait(timing.pollIntervalMs);
        const statusPayload = await apiFetch(
          `/module-icons/generations/${model}/${requestId}/status`,
          {
            method: "GET",
          },
        );
        const status = statusPayload.data?.status || "UNKNOWN";

        setGeneration({
          loading: true,
          status,
          requestId,
          historyId,
          model,
          startedAt: createdAt,
        });
        appendGenerationStatusResponse(
          historyId,
          status,
          statusPayload,
          new Date().toISOString(),
        );

        if (status === "COMPLETED") {
          completed = true;
          break;
        }

        if (status === "FAILED" || status === "ERROR") {
          throw new Error("Generazione fal.ai fallita");
        }
      }

      if (!completed) {
        patchAiHistoryRecord(historyId, {
          status: "STILL_RUNNING",
          pausedAt: new Date().toISOString(),
          error: null,
        });
        setGeneration({
          loading: false,
          status: "STILL_RUNNING",
          requestId,
          historyId,
          model,
          startedAt: createdAt,
        });
        message.info(
          "Generazione ancora in corso su fal.ai: puoi ricontrollarla dai risultati",
        );
        setAiStep(2);
        return;
      }

      await completeGenerationRecord({
        historyId,
        recordModel: model,
        requestId,
      });
      setGeneration({
        loading: false,
        status: "COMPLETED",
        requestId,
        historyId,
        model,
        startedAt: createdAt,
      });
      message.success("Generazione completata: scegli una risposta");
      setAiStep(2);
    } catch (error) {
      console.error("Error generating module icon:", error);
      patchAiHistoryRecord(historyId, {
        status: "FAILED",
        failedAt: new Date().toISOString(),
        error: error.message || "Errore durante la generazione",
      });
      setGeneration((current) => ({
        ...current,
        loading: false,
        status: "FAILED",
      }));
      message.error(error.message || "Errore durante la generazione");
    }
  };

  const selectGeneratedImage = async (record, image, imageIndex) => {
    if (!image?.url) {
      message.warning("Risposta AI senza URL immagine");
      return;
    }

    const choiceKey = `${record.id}-${imageIndex}`;
    const removeBackground = Boolean(
      record.removeBackground && isPngAiModel(record.model),
    );
    const assetKey = getAssetFetchKey(image.url, removeBackground);

    try {
      setChoosingImageKey(choiceKey);
      const { assetData } = await importGeneratedAsset(image, removeBackground);
      requestedThumbUrlsRef.current.add(assetKey);
      setAiPreviewAssets((current) => ({
        ...current,
        [assetKey]: {
          loading: false,
          src: assetData?.dataUri,
          contentType: assetData?.contentType,
        },
      }));
      patchAiHistoryRecord(record.id, {
        assetProxyResponse: {
          sourceUrl: image.url,
          contentType: assetData?.contentType,
          fileName: image.fileName || "generated-icon",
          hasSvgText: Boolean(assetData?.text),
          backgroundRemoved: Boolean(assetData?.backgroundRemoved),
          backgroundRemovalProvider: assetData?.backgroundRemovalProvider,
        },
      });
      setAssetMode("ai");
      setAiModalOpen(false);
      setBuilderStep(2);
      message.success("Icona caricata nel builder");
    } catch (error) {
      console.error("Error importing generated icon:", error);
      message.error(
        error.message || "Errore durante il caricamento dell'icona",
      );
    } finally {
      setChoosingImageKey(null);
    }
  };

  const applySvgInput = () => {
    const svg = svgInput.trim();

    if (!svg.includes("<svg")) {
      message.warning("Incolla un SVG valido");
      return;
    }

    setAsset({
      href: svgToDataUri(svg),
      contentType: "image/svg+xml",
      text: svg,
      svgText: svg,
      fileName: "custom-icon.svg",
      label: "SVG manuale",
    });
    message.success("SVG caricato nel builder");
  };

  const loadSvgFile = (file) => {
    const isSvg =
      file.type === "image/svg+xml" || /\.svg$/i.test(file.name || "");

    if (!isSvg) {
      message.warning("Carica un file SVG");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const svg = String(reader.result || "").trim();

      if (!svg.includes("<svg")) {
        message.warning("File SVG non valido");
        return;
      }

      setSvgInput(svg);
      setAsset({
        href: svgToDataUri(svg),
        contentType: "image/svg+xml",
        text: svg,
        svgText: svg,
        fileName: file.name || "custom-icon.svg",
        label: file.name || "SVG caricato",
      });
      message.success("SVG caricato nel builder");
    };

    reader.onerror = () => {
      message.error("Impossibile leggere il file SVG");
    };

    reader.readAsText(file);
  };

  const applyUrlInput = () => {
    const imageUrl = urlInput.trim();

    if (!/^https?:\/\//i.test(imageUrl) && !imageUrl.startsWith("data:")) {
      message.warning("Inserisci un URL immagine valido");
      return;
    }

    setAsset({
      href: imageUrl,
      sourceUrl: imageUrl,
      contentType: null,
      text: null,
      fileName: "remote-icon",
      label: "URL immagine",
    });
    message.success("URL caricato nel builder");
  };

  const exportAll = async () => {
    if (!asset) {
      message.warning("Scegli prima un'icona centrale");
      return;
    }

    try {
      setExporting(true);
      const baseName = sanitizeFilename(
        selectedCategory?.name || "module-icon",
      );
      const fullSvg = buildModuleIconSvg({
        asset,
        builder,
        includeBackground: true,
      });
      const glyphSvg = buildModuleIconSvg({
        asset,
        builder,
        includeBackground: false,
      });

      downloadText(`${baseName}-full.svg`, fullSvg, "image/svg+xml");
      downloadText(`${baseName}-glyph.svg`, glyphSvg, "image/svg+xml");

      const [fullPng, glyphPng] = await Promise.all([
        renderSvgToPngBlob(fullSvg, builder.size),
        renderSvgToPngBlob(glyphSvg, builder.size),
      ]);

      downloadBlob(`${baseName}-full-${builder.size}.png`, fullPng);
      downloadBlob(`${baseName}-glyph-${builder.size}.png`, glyphPng);

      message.success("Export completato: SVG e PNG, pieno e solo icona");
    } catch (error) {
      console.error("Error exporting module icon:", error);
      message.error(error.message || "Errore durante l'export");
    } finally {
      setExporting(false);
    }
  };

  // Reuses the same presigned-S3 upload the Modules editor relies on:
  // POST /upload returns a presigned URL, we PUT the blob, then the file is
  // referenced by its S3 key (name) on config.files.
  const uploadGeneratedIcon = async (blob, filename) => {
    const token = await getTokenSilently();
    const presign = await fetch(`${apiBaseUrl}/upload`, {
      method: "post",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        filename,
        contentType: blob.type || "image/png",
        collection: "modules",
      }),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Inizializzazione upload fallita (${response.status})`);
      }
      return response.json();
    });

    const uploadUrl = presign.result?.url;
    const method = presign.result?.method || "PUT";

    if (!uploadUrl) {
      throw new Error("URL di upload non disponibile");
    }

    const putResponse = await fetch(uploadUrl, {
      method,
      headers: { "Content-Type": blob.type || "image/png" },
      body: blob,
    });

    if (!putResponse.ok) {
      throw new Error(`Upload fallito (${putResponse.status})`);
    }

    const s3Path = new URL(uploadUrl).pathname;

    return {
      fileName: s3Path.substring(1),
      url: `${config.files[env]}${s3Path}`,
    };
  };

  const applyIconToModule = async () => {
    if (!asset) {
      message.warning("Scegli prima un'icona centrale");
      return;
    }

    const target = modules.find((module) => module.objectId === applyTargetId);

    if (!target) {
      message.warning("Seleziona un modulo");
      return;
    }

    try {
      setApplying(true);
      const fullSvg = buildModuleIconSvg({
        asset,
        builder,
        includeBackground: true,
      });
      const pngBlob = await renderSvgToPngBlob(fullSvg, builder.size);
      const baseName = sanitizeFilename(target.name || "module-icon");
      const { fileName, url } = await uploadGeneratedIcon(
        pngBlob,
        `${baseName}.png`,
      );

      const token = await getTokenSilently();
      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query: UPDATE_MODULE_ICON_MUTATION,
          variables: {
            id: target.objectId,
            fields: { icon: { file: fileName } },
          },
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.errors) {
        throw new Error(
          response.data.errors.map((item) => item.message).join(", "),
        );
      }

      setModules((current) =>
        current.map((module) =>
          module.objectId === target.objectId
            ? {
                ...module,
                icon: { ...(module.icon || {}), name: fileName, url },
                iconUrl: url,
              }
            : module,
        ),
      );
      message.success(`Icona applicata a “${target.name}”`);
      setApplyModalOpen(false);
    } catch (error) {
      console.error("Error applying icon to module:", error);
      message.error(
        error.message || "Errore durante l'applicazione dell'icona",
      );
    } finally {
      setApplying(false);
    }
  };

  const copyModuleIconUrl = async (module) => {
    if (!module.iconUrl) {
      message.warning("Questo modulo non ha un'icona");
      return;
    }

    try {
      await copyTextToClipboard(module.iconUrl);
      message.success("URL icona copiato");
    } catch (error) {
      console.error("Error copying module icon URL:", error);
      message.error("Impossibile copiare l'URL");
    }
  };

  const downloadModuleIcon = async (module) => {
    if (!module.iconUrl) {
      message.warning("Questo modulo non ha un'icona");
      return;
    }

    try {
      const response = await fetch(module.iconUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      downloadBlob(getModuleIconFilename(module, blob.type), blob);
      message.success("Download icona avviato");
    } catch (error) {
      console.warn("Blob download failed, falling back to direct link:", error);
      directDownload(getModuleIconFilename(module), module.iconUrl);
      message.warning("Download diretto avviato");
    }
  };

  const renderModuleCard = (module) => (
    <div className="icons-card" key={module.objectId}>
      <div
        className={`icons-module-icon${
          module.iconUrl ? "" : " icons-module-icon--empty"
        }`}
      >
        {module.iconUrl ? (
          <img src={module.iconUrl} alt={module.name} loading="lazy" />
        ) : (
          <div className="icons-module-missing">
            <FileImageOutlined className="icons-module-missing-icon" />
            <span className="icons-module-missing-label">Icona mancante</span>
          </div>
        )}
      </div>
      <div className="icons-module-copy">
        <Text
          strong
          className="icons-module-name icons-module-name--link"
          title={`Apri “${module.name}” nei Moduli`}
          onClick={() =>
            history.push(`/settings/modules?edit=${module.objectId}`)
          }
        >
          {module.name}
        </Text>
        <div className="icons-module-actions">
          <Button
            className="icons-module-edit"
            size="small"
            type={module.iconUrl ? "default" : "primary"}
            icon={module.iconUrl ? <EditOutlined /> : <PlusOutlined />}
            onClick={() => openBuilderForModule(module)}
          >
            {module.iconUrl ? "Modifica" : "Crea icona"}
          </Button>
          {module.iconUrl && (
            <div className="icons-module-tools">
              <Tooltip title="Copia URL">
                <span>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    aria-label={`Copia URL icona ${module.name}`}
                    onClick={() => copyModuleIconUrl(module)}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Scarica">
                <span>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    aria-label={`Scarica icona ${module.name}`}
                    onClick={() => downloadModuleIcon(module)}
                  />
                </span>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderBuilderSource = () => {
    if (assetMode === "svg") {
      return (
        <Space direction="vertical" style={{ width: "100%" }} size={10}>
          <Upload.Dragger
            accept=".svg,image/svg+xml"
            multiple={false}
            showUploadList={false}
            beforeUpload={(file) => {
              loadSvgFile(file);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Trascina un SVG qui o clicca per selezionarlo
            </p>
            <p className="ant-upload-hint">Solo file .svg</p>
          </Upload.Dragger>
          <Text type="secondary">Oppure incolla il codice SVG</Text>
          <TextArea
            rows={4}
            value={svgInput}
            onChange={(event) => setSvgInput(event.target.value)}
            placeholder="<svg ...>...</svg>"
          />
          <Button icon={<FileImageOutlined />} onClick={applySvgInput} block>
            Usa SVG incollato
          </Button>
        </Space>
      );
    }

    if (assetMode === "url") {
      return (
        <Space.Compact style={{ width: "100%" }}>
          <Input
            value={urlInput}
            onChange={(event) => setUrlInput(event.target.value)}
            placeholder="https://..."
          />
          <Button icon={<PictureOutlined />} onClick={applyUrlInput}>
            Usa
          </Button>
        </Space.Compact>
      );
    }

    return null;
  };

  const renderAiHistoryRecord = (record, isHero = false) => {
    const images = record.images || record.resultResponse?.data?.images || [];
    const statusColor =
      record.status === "COMPLETED"
        ? "green"
        : isGenerationErrorStatus(record.status)
          ? "red"
          : "blue";
    const timing = getModelTiming(record.model);
    const elapsedSeconds = getGenerationElapsedSeconds(record, nowMs);
    const progressPercent = getGenerationProgressPercent(record, nowMs);
    const canRefreshGeneration = Boolean(
      record.requestId &&
        record.model &&
        record.status !== "COMPLETED" &&
        !isGenerationErrorStatus(record.status),
    );
    const apiDetails = {
      request: record.request,
      submitResponse: record.submitResponse,
      statusResponses: record.statusResponses || [],
      resultResponse: record.resultResponse,
      assetProxyResponse: record.assetProxyResponse,
      error: record.error,
    };

    return (
      <div
        className={`icons-ai-run${isHero ? " icons-ai-run--hero" : ""}`}
        key={record.id}
      >
        <div className="icons-ai-run-head">
          <div>
            <Space size={[6, 6]} wrap>
              <Text strong>
                {record.modelLabel || getModelLabel(record.model)}
              </Text>
              <Tag color={statusColor}>
                {STATUS_LABELS[record.status] || record.status}
              </Tag>
              {record.removeBackground && isPngAiModel(record.model) && (
                <Tag color="cyan">Pixian</Tag>
              )}
            </Space>
            <br />
            <Text type="secondary">
              {formatHistoryDate(record.createdAt)}
              {record.categoryName ? ` · ${record.categoryName}` : ""}
            </Text>
          </div>
          {canRefreshGeneration && (
            <div className="icons-ai-run-actions">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={checkingGenerationId === record.id}
                onClick={() => refreshGenerationRecord(record)}
              >
                Aggiorna
              </Button>
            </div>
          )}
        </div>

        <Text className="icons-ai-prompt">{record.prompt}</Text>

        <div className="icons-ai-run-progress">
          <Progress
            percent={progressPercent}
            status={getProgressStatus(record.status)}
            size="small"
          />
          <div className="icons-ai-progress-meta">
            <Text type="secondary">
              Tempo {formatDuration(elapsedSeconds)}
            </Text>
            <Text type="secondary">
              Stima {formatDuration(timing.expectedSeconds)}
            </Text>
          </div>
        </div>

        {record.error && <Alert type="error" showIcon message={record.error} />}

        {images.length > 0 ? (
          <div className="icons-ai-response-grid">
            {images.map((image, imageIndex) => {
              const choiceKey = `${record.id}-${imageIndex}`;
              const removeBackground = Boolean(
                record.removeBackground && isPngAiModel(record.model),
              );
              const previewKey = image?.url
                ? getAssetFetchKey(image.url, removeBackground)
                : null;
              const previewAsset = previewKey
                ? aiPreviewAssets[previewKey]
                : null;

              return (
                <div
                  className="icons-ai-result-card"
                  key={image.url || choiceKey}
                >
                  <div className="icons-ai-result-thumb">
                    {previewAsset?.src ? (
                      <img
                        src={previewAsset.src}
                        alt={image.fileName || "Icona AI"}
                        loading="lazy"
                      />
                    ) : image?.url && !previewAsset?.error ? (
                      <Spin size="small" />
                    ) : image?.url ? (
                      <span className="icons-ai-thumb-fallback">
                        <FileImageOutlined />
                        Anteprima non disponibile
                      </span>
                    ) : (
                      <span className="icons-module-empty" />
                    )}
                  </div>
                  <Button
                    size="small"
                    loading={choosingImageKey === choiceKey}
                    onClick={() =>
                      selectGeneratedImage(record, image, imageIndex)
                    }
                    block
                  >
                    Usa
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          record.status === "COMPLETED" && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nessuna immagine"
            />
          )
        )}

        {record.requestId && (
          <Text
            type="secondary"
            className="icons-ai-meta icons-ellipsis"
            title={record.requestId}
          >
            ID: {record.requestId}
          </Text>
        )}

        <details className="icons-ai-details">
          <summary>Chiamate e risposte</summary>
          <pre>{safeJson(apiDetails)}</pre>
        </details>
      </div>
    );
  };

  const renderStepCategory = () => (
    <div className="icons-tab-stack">
      <div>
        <div className="icons-field-label">
          <Text strong>Categoria</Text>
        </div>
        <Select
          value={builder.categoryId}
          onChange={applyCategory}
          style={{ width: "100%" }}
          options={categories.map((category) => {
            const tone = getCategoryTone(category);
            return {
              value: category.objectId,
              label: (
                <Space>
                  <span
                    className="icons-mini-swatch"
                    style={{
                      backgroundColor: tone.backgroundColor,
                      border: `2px solid ${tone.foregroundColor}`,
                    }}
                  />
                  <span>{category.name}</span>
                </Space>
              ),
            };
          })}
        />
      </div>
      <Text type="secondary">
        La categoria imposta il tema colore di sfondo e icona. Potrai
        modificarlo nello step di rifinitura.
      </Text>
    </div>
  );

  const renderStepSource = () => (
    <div className="icons-tab-stack">
      {generation.requestId && (
        <div className="icons-section-head">
          <Text type="secondary">Generazione AI</Text>
          <Tag color={generation.status === "COMPLETED" ? "green" : "blue"}>
            {STATUS_LABELS[generation.status] || generation.status}
          </Tag>
        </div>
      )}
      <div className="icons-source-grid">
        <Button
          icon={<RocketOutlined />}
          onClick={() => {
            setAssetMode("ai");
            setAiStep(0);
            setAiModalOpen(true);
          }}
        >
          AI
        </Button>
        <Button
          icon={<FileImageOutlined />}
          onClick={() => setAssetMode("svg")}
        >
          SVG
        </Button>
        <Button icon={<PictureOutlined />} onClick={() => setAssetMode("url")}>
          URL
        </Button>
      </div>

      {renderBuilderSource()}

      {asset ? (
        <div className="icons-asset-strip">
          <div className="icons-asset-thumb">
            <img src={asset.href} alt={asset.label || "asset"} />
          </div>
          <div className="icons-ellipsis">
            <Text strong>{asset.label || "Icona centrale"}</Text>
            <br />
            <Text type="secondary">
              {asset.contentType || "Risorsa esterna"}
            </Text>
          </div>
        </div>
      ) : (
        <Text type="secondary" className="icons-source-hint">
          Scegli AI, incolla un SVG o usa un URL immagine.
          {aiHistory.length > 0
            ? ` Risposte AI salvate: ${aiHistory.length}.`
            : ""}
        </Text>
      )}
    </div>
  );

  const renderStepRefine = () => (
    <div className="icons-tab-stack">
      <div className="icons-two-col">
        <div>
          <div className="icons-field-label">
            <Text strong>
              <BgColorsOutlined /> Sfondo
            </Text>
          </div>
          <ColorPicker
            value={builder.backgroundColor}
            onChange={(color) =>
              updateBuilder({
                backgroundColor: normalizeHex(
                  color.toHexString(),
                  builder.backgroundColor,
                ),
              })
            }
            showText
          />
        </div>
        <div>
          <div className="icons-field-label">
            <Text strong>
              <HighlightOutlined /> Icona
            </Text>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto" }}
              disabled={
                isRasterAsset ||
                builder.foregroundColor === categoryStandardForeground
              }
              onClick={() =>
                updateBuilder({ foregroundColor: categoryStandardForeground })
              }
            >
              Ripristina
            </Button>
          </div>
          <ColorPicker
            value={builder.foregroundColor}
            disabled={isRasterAsset}
            onChange={(color) =>
              updateBuilder({
                foregroundColor: normalizeHex(
                  color.toHexString(),
                  builder.foregroundColor,
                ),
              })
            }
            showText
          />
          {isRasterAsset ? (
            <Text
              type="secondary"
              style={{ display: "block", marginTop: 4, fontSize: 11 }}
            >
              Non applicabile alle icone PNG
            </Text>
          ) : (
            <Select
              size="small"
              style={{ width: "100%", marginTop: 6 }}
              placeholder="Standard di una categoria"
              value={matchingStandardCategoryId}
              onChange={(categoryId) => {
                const category = categories.find(
                  (item) => item.objectId === categoryId,
                );

                if (category) {
                  updateBuilder({
                    foregroundColor: getCategoryTone(category).foregroundColor,
                  });
                }
              }}
              options={categories.map((category) => {
                const tone = getCategoryTone(category);
                return {
                  value: category.objectId,
                  label: (
                    <Space>
                      <span
                        className="icons-mini-swatch"
                        style={{
                          backgroundColor: tone.foregroundColor,
                          border: "1px solid rgba(24, 39, 47, 0.15)",
                        }}
                      />
                      <span>{category.name}</span>
                    </Space>
                  ),
                };
              })}
            />
          )}
        </div>
      </div>

      <Row gutter={12}>
        <Col span={12}>
          <div className="icons-field-label">
            <Text strong>Dimensione output</Text>
          </div>
          <InputNumber
            min={128}
            max={2048}
            step={128}
            value={builder.size}
            onChange={(value) => updateBuilder({ size: value || 512 })}
            style={{ width: "100%" }}
          />
        </Col>
        <Col span={12}>
          <div className="icons-field-label">
            <Text strong>Dimensione icona</Text>
            <Text type="secondary">{builder.scalePercent}%</Text>
          </div>
          <Slider
            min={ICON_SCALE_MIN}
            max={ICON_SCALE_MAX}
            value={builder.scalePercent}
            onChange={(value) => updateBuilder({ scalePercent: value })}
            tooltip={{ formatter: (value) => `${value}%` }}
          />
        </Col>
      </Row>

      <div>
        <div className="icons-field-label">
          <Text strong>Posizione</Text>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: "auto" }}
            disabled={
              builder.offsetXPercent === 0 && builder.offsetYPercent === 0
            }
            onClick={() =>
              updateBuilder({
                offsetXPercent: 0,
                offsetYPercent: 0,
              })
            }
          >
            Centra
          </Button>
        </div>
        <Row gutter={12}>
          <Col span={12}>
            <Text type="secondary">Orizzontale</Text>
            <Slider
              min={-ICON_OFFSET_RANGE}
              max={ICON_OFFSET_RANGE}
              value={builder.offsetXPercent}
              onChange={(value) => updateBuilder({ offsetXPercent: value })}
              tooltip={{ formatter: (value) => `${value}%` }}
            />
          </Col>
          <Col span={12}>
            <Text type="secondary">Verticale</Text>
            <Slider
              min={-ICON_OFFSET_RANGE}
              max={ICON_OFFSET_RANGE}
              value={builder.offsetYPercent}
              onChange={(value) => updateBuilder({ offsetYPercent: value })}
              tooltip={{ formatter: (value) => `${value}%` }}
            />
          </Col>
        </Row>
      </div>
    </div>
  );

  const renderBuilderPanel = () => (
    <div className="icons-builder">
      <Steps
        className="icons-builder-steps"
        current={builderStep}
        onChange={setBuilderStep}
        responsive={false}
        items={BUILDER_STEPS.map((step) => ({
          title: step.title,
          description: step.description,
        }))}
      />
      <div className="icons-builder-main">
        <div className="icons-preview-col">
          <div className="icons-preview-zone">
            <div
              className="icons-preview-large"
              dangerouslySetInnerHTML={{ __html: fullPreviewSvg }}
            />
            {asset ? (
              <div
                className="icons-preview-small"
                dangerouslySetInnerHTML={{ __html: glyphPreviewSvg }}
              />
            ) : (
              <div className="icons-preview-small icons-preview-glyph-empty">
                <PictureOutlined />
              </div>
            )}
          </div>

          <div className="icons-preview-toggle">
            <Checkbox
              checked={showAlignmentGrid}
              onChange={(event) => setShowAlignmentGrid(event.target.checked)}
            >
              Griglia di allineamento
            </Checkbox>
          </div>

          {!asset && (
            <Text type="secondary" className="icons-preview-note">
              Sfondo in anteprima. Scegli una sorgente per aggiungere
              l&apos;icona.
            </Text>
          )}

          <div className="icons-builder-actions">
            <Tooltip title="Scarica SVG e PNG nei due formati">
              <Button
                type="primary"
                size="large"
                icon={<DownloadOutlined />}
                loading={exporting}
                disabled={!asset}
                onClick={exportAll}
                block
              >
                Esporta SVG + PNG
              </Button>
            </Tooltip>
            <Button
              size="large"
              icon={<AppstoreOutlined />}
              disabled={!asset}
              onClick={() => {
                setApplyTargetId(linkedModuleId);
                setApplyModalOpen(true);
              }}
              block
            >
              {linkedModule
                ? `Applica a ${linkedModule.name}`
                : "Applica a modulo"}
            </Button>
            <Tooltip title="Apri la cartella Drive dove raccogliamo le icone">
              <Button
                icon={<FolderOpenOutlined />}
                href={DRIVE_FOLDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                block
              >
                Apri cartella Drive
              </Button>
            </Tooltip>
          </div>
        </div>

        <div className="icons-builder-tabs">
          {builderStep === 0 && renderStepCategory()}
          {builderStep === 1 && renderStepSource()}
          {builderStep === 2 && renderStepRefine()}

          <div className="icons-builder-nav">
            <Button
              disabled={builderStep === 0}
              onClick={() => setBuilderStep((step) => Math.max(0, step - 1))}
            >
              Indietro
            </Button>
            {builderStep < BUILDER_STEPS.length - 1 && (
              <Button
                type="primary"
                onClick={() =>
                  setBuilderStep((step) =>
                    Math.min(BUILDER_STEPS.length - 1, step + 1),
                  )
                }
              >
                Avanti
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const aiError = isGenerationErrorStatus(generation.status);
  const aiStatusLabel = STATUS_LABELS[generation.status] || "In corso";
  const aiShowStatus = Boolean(
    generation.requestId || generation.loading || generation.status,
  );
  const canRemoveAiPngBackground = isPngAiModel(model);
  const isVectorModel = getModelOption(model)?.tag === "SVG";
  const autoImagePrompt = buildImagePrompt({
    subject: prompt.trim(),
    foregroundColor: builder.foregroundColor,
    foregroundColorLight: builder.foregroundColorLight,
    isVector: isVectorModel,
    iconStyle: builder.iconStyle,
    strokeWeight: builder.strokeWeight,
    look: builder.look,
    detail: builder.detail,
    corners: builder.corners,
    perspective: builder.perspective,
  });
  const finalImagePrompt =
    imagePromptOverride != null ? imagePromptOverride : autoImagePrompt;
  const applyTarget = applyTargetId
    ? modules.find((module) => module.objectId === applyTargetId)
    : null;
  const linkedModule = linkedModuleId
    ? modules.find((module) => module.objectId === linkedModuleId)
    : null;
  const modelSupportsVariants = isPngAiModel(model);
  const effectiveVariantCount = modelSupportsVariants ? variantCount : 1;
  const selectedTiming = getModelTiming(model);
  const activeGenerationRecord =
    aiHistory.find((record) => record.id === generation.historyId) ||
    (generation.status
      ? {
          id: generation.historyId,
          model: generation.model || model,
          status: generation.status,
          startedAt: generation.startedAt,
        }
      : null);
  const activeTiming = getModelTiming(activeGenerationRecord?.model || model);
  const activeElapsedSeconds = activeGenerationRecord
    ? getGenerationElapsedSeconds(activeGenerationRecord, nowMs)
    : 0;
  const activeProgressPercent = activeGenerationRecord
    ? getGenerationProgressPercent(activeGenerationRecord, nowMs)
    : 0;

  const renderAiStepDescribe = () => (
    <div className="icons-ai-step">
      <div className="icons-ai-step-head">
        <Text strong>Descrivi l&apos;icona</Text>
        <span className="icons-ai-context-chip">
          <span
            className="icons-mini-swatch"
            style={{
              backgroundColor: builder.backgroundColor,
              border: `2px solid ${builder.foregroundColor}`,
            }}
          />
          <Text type="secondary">
            {selectedCategory?.name || "Senza categoria"}
          </Text>
        </span>
      </div>

      <TextArea
        rows={5}
        value={prompt}
        onChange={(event) => changeSubjectPrompt(event.target.value)}
        placeholder="Scrivi un'idea breve (es. firma digitale) o un prompt completo..."
      />

      <div className="icons-ai-improve">
        <Button
          icon={<BulbOutlined />}
          loading={promptLoading}
          disabled={!prompt.trim()}
          onClick={improvePrompt}
        >
          Migliora con AI
        </Button>
        {previousPrompt !== null && (
          <Button
            type="link"
            size="small"
            onClick={() => {
              changeSubjectPrompt(previousPrompt);
              setPreviousPrompt(null);
            }}
          >
            Annulla
          </Button>
        )}
        <Text type="secondary">
          L&apos;AI (gpt-5-chat) riscrive il testo in un prompt curato.
        </Text>
      </div>

      <details className="icons-ai-system">
        <summary>Contesto AI (system prompt)</summary>
        <Text type="secondary" className="icons-ai-system-hint">
          Inviato all&apos;AI per spiegare il contesto OFC. Puoi modificarlo.
        </Text>
        <TextArea
          rows={6}
          value={systemPrompt}
          onChange={(event) => setSystemPrompt(event.target.value)}
        />
        {systemPrompt !== DEFAULT_AI_SYSTEM_PROMPT && (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: "auto" }}
            onClick={() => setSystemPrompt(DEFAULT_AI_SYSTEM_PROMPT)}
          >
            Ripristina predefinito
          </Button>
        )}
      </details>

      <div className="icons-ai-nav">
        <span />
        <Button
          type="primary"
          disabled={!prompt.trim()}
          onClick={() => setAiStep(1)}
        >
          Avanti
        </Button>
      </div>
    </div>
  );

  const renderAiStepGenerate = () => (
    <div className="icons-ai-step">
      <div className="icons-ai-field">
        <div className="icons-field-label">
          <Text strong>Modello</Text>
        </div>
        <Select
          value={model}
          onChange={setModel}
          style={{ width: "100%" }}
          labelRender={({ value }) => {
            const option = getModelOption(value);

            if (!option) {
              return value;
            }

            return (
              <span>
                {option.label}
                <span className="icons-ai-model-format">{option.tag}</span>
              </span>
            );
          }}
          options={MODEL_OPTIONS.map((option) => ({
            value: option.value,
            label: (
              <Space>
                <span>{option.label}</span>
                <Tag>{option.tag}</Tag>
              </Space>
            ),
          }))}
        />
        <div className="icons-ai-estimate">
          <div className="icons-ai-estimate-row">
            <Text type="secondary">Stima attesa</Text>
            <Text strong>{formatDuration(selectedTiming.expectedSeconds)}</Text>
          </div>
          <Text type="secondary">{selectedTiming.note}</Text>
        </div>
      </div>

      <div className="icons-ai-field">
        <div className="icons-field-label">
          <Text strong>Varianti</Text>
          {!modelSupportsVariants && (
            <Text type="secondary">Recraft genera 1 SVG</Text>
          )}
        </div>
        <Segmented
          block
          value={modelSupportsVariants ? variantCount : 1}
          disabled={!modelSupportsVariants}
          onChange={setVariantCount}
          options={[1, 2, 3, 4].map((count) => ({
            label: count === 1 ? "1 (singola)" : `${count}`,
            value: count,
          }))}
        />
      </div>

      <div className="icons-ai-field">
        <div className="icons-field-label">
          <Text strong>Stile</Text>
        </div>
        <Segmented
          block
          value={builder.iconStyle}
          onChange={(value) => updateBuilder({ iconStyle: value })}
          options={ICON_STYLE_OPTIONS}
        />
      </div>

      {builder.iconStyle !== "filled" && (
        <div className="icons-ai-field">
          <div className="icons-field-label">
            <Text strong>Tratto</Text>
          </div>
          <Segmented
            block
            value={builder.strokeWeight}
            onChange={(value) => updateBuilder({ strokeWeight: value })}
            options={STROKE_WEIGHT_OPTIONS}
          />
        </div>
      )}

      {builder.iconStyle === "duotone" && (
        <div className="icons-ai-field">
          <div className="icons-field-label">
            <Text strong>Tinta chiara (riempimenti)</Text>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto" }}
              onClick={() =>
                updateBuilder({
                  foregroundColorLight: lightenHex(builder.foregroundColor),
                })
              }
            >
              Dal colore principale
            </Button>
          </div>
          <ColorPicker
            value={builder.foregroundColorLight}
            onChange={(color) =>
              updateBuilder({
                foregroundColorLight: normalizeHex(
                  color.toHexString(),
                  builder.foregroundColorLight,
                ),
              })
            }
            showText
          />
        </div>
      )}

      <details className="icons-ai-system">
        <summary>Stile avanzato</summary>
        <div className="icons-ai-advanced">
          <div className="icons-ai-field">
            <div className="icons-field-label">
              <Text strong>Look</Text>
            </div>
            <Segmented
              block
              value={builder.look}
              onChange={(value) => updateBuilder({ look: value })}
              options={LOOK_OPTIONS}
            />
          </div>
          <div className="icons-ai-field">
            <div className="icons-field-label">
              <Text strong>Dettaglio</Text>
            </div>
            <Segmented
              block
              value={builder.detail}
              onChange={(value) => updateBuilder({ detail: value })}
              options={DETAIL_OPTIONS}
            />
          </div>
          <div className="icons-ai-field">
            <div className="icons-field-label">
              <Text strong>Angoli</Text>
            </div>
            <Segmented
              block
              value={builder.corners}
              onChange={(value) => updateBuilder({ corners: value })}
              options={CORNER_OPTIONS}
            />
          </div>
          <div className="icons-ai-field">
            <div className="icons-field-label">
              <Text strong>Prospettiva</Text>
            </div>
            <Segmented
              block
              value={builder.perspective}
              onChange={(value) => updateBuilder({ perspective: value })}
              options={PERSPECTIVE_OPTIONS}
            />
          </div>
        </div>
      </details>

      <div>
        <Checkbox
          checked={removeAiPngBackground}
          disabled={!canRemoveAiPngBackground}
          onChange={(event) => setRemoveAiPngBackground(event.target.checked)}
        >
          Rimuovi sfondo con Pixian
        </Checkbox>
        <br />
        <Text type="secondary">
          Solo per output PNG; Recraft SVG resta già bucato.
        </Text>
      </div>

      <div className="icons-ai-field">
        <div className="icons-field-label">
          <Text strong>Prompt inviato al modello</Text>
          {imagePromptOverride !== null && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto" }}
              onClick={() => setImagePromptOverride(null)}
            >
              Ripristina dal soggetto
            </Button>
          )}
        </div>
        <TextArea
          rows={6}
          value={finalImagePrompt}
          onChange={(event) => setImagePromptOverride(event.target.value)}
        />
        <Text type="secondary" style={{ marginTop: 4 }}>
          Modificabile: questo testo viene inviato così com&apos;è al modello.
        </Text>
      </div>

      {aiShowStatus && activeGenerationRecord && (
        <div className="icons-ai-status">
          <div className="icons-ai-status-head">
            <Text strong>Stato</Text>
            <Tag
              color={
                aiError
                  ? "red"
                  : activeGenerationRecord.status === "COMPLETED"
                    ? "green"
                    : "blue"
              }
            >
              {aiStatusLabel}
            </Tag>
          </div>
          <Progress
            percent={activeProgressPercent}
            status={getProgressStatus(activeGenerationRecord.status)}
            size="small"
          />
          <div className="icons-ai-progress-meta">
            <Text type="secondary">
              Trascorsi {formatDuration(activeElapsedSeconds)}
            </Text>
            <Text type="secondary">
              Stima {formatDuration(activeTiming.expectedSeconds)}
            </Text>
          </div>
        </div>
      )}

      <div className="icons-ai-nav">
        <Button onClick={() => setAiStep(0)}>Indietro</Button>
        <Button
          type="primary"
          icon={<RocketOutlined />}
          loading={generation.loading}
          disabled={!prompt.trim()}
          onClick={generateAsset}
        >
          {effectiveVariantCount > 1
            ? `Genera ${effectiveVariantCount} icone`
            : "Genera icona"}
        </Button>
      </div>
    </div>
  );

  const renderAiStepChoose = () => (
    <div className="icons-ai-step">
      {aiHistory.length > 0 ? (
        <>
          {renderAiHistoryRecord(aiHistory[0], true)}
          {aiHistory.length > 1 && (
            <details className="icons-ai-older">
              <summary>{aiHistory.length - 1} generazioni precedenti</summary>
              <div className="icons-ai-run-list">
                {aiHistory.slice(1).map((record) => renderAiHistoryRecord(record))}
              </div>
            </details>
          )}
          <div className="icons-ai-history-foot">
            <Button size="small" danger onClick={() => setAiHistory([])}>
              Svuota storico
            </Button>
          </div>
        </>
      ) : (
        <div className="icons-empty-state">
          <Empty description="Nessuna generazione: torna a “Descrivi” per iniziare" />
        </div>
      )}

      <div className="icons-ai-nav">
        <Button onClick={() => setAiStep(1)}>Indietro</Button>
        <span />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  const missingIconCount = modules.filter((module) => !module.iconUrl).length;
  const withIconCount = modules.length - missingIconCount;

  return (
    <div className="icons-page">
      <style>{styles}</style>

      <section className="icons-head">
        <div className="icons-head-copy">
          <div className="icons-head-title">
            <span className="icons-head-mark">
              <AppstoreOutlined />
            </span>
            <div>
              <Title level={2} style={{ margin: 0 }}>
                Icone
              </Title>
              <Text type="secondary">
                Libreria moduli e builder guidato per nuove icone OFC.
              </Text>
            </div>
          </div>
        </div>
        <div className="icons-head-stats">
          <div className="icons-stat">
            <Text type="secondary">Moduli</Text>
            <Title level={3} style={{ margin: 0 }}>
              {modules.length}
            </Title>
          </div>
          <div className="icons-stat">
            <Text type="secondary">Icone</Text>
            <Title level={3} style={{ margin: 0 }}>
              {modules.filter((module) => module.iconUrl).length}
            </Title>
          </div>
          <div className="icons-stat">
            <Text type="secondary">Categorie</Text>
            <Title level={3} style={{ margin: 0 }}>
              {categories.length}
            </Title>
          </div>
        </div>
      </section>

      <div className="icons-layout">
        <section className="icons-library">
          <div className="icons-toolbar">
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Cerca icona o categoria"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={[
                { value: "all", label: "Tutte le categorie" },
                ...categories.map((category) => {
                  const tone = getCategoryTone(category);
                  return {
                    value: category.objectId,
                    label: (
                      <Space>
                        <span
                          className="icons-mini-swatch"
                          style={{
                            backgroundColor: tone.backgroundColor,
                            border: `2px solid ${tone.foregroundColor}`,
                          }}
                        />
                        <span>{category.name}</span>
                      </Space>
                    ),
                  };
                }),
              ]}
            />
            <Segmented
              value={iconStatusFilter}
              onChange={setIconStatusFilter}
              options={[
                { label: "Tutte", value: "all" },
                { label: `Con icona (${withIconCount})`, value: "with" },
                { label: `Mancanti (${missingIconCount})`, value: "missing" },
              ]}
            />
            <Button
              type="primary"
              icon={<HighlightOutlined />}
              onClick={() => {
                resetBuilder();
                setBuilderModalOpen(true);
              }}
            >
              Nuova icona
            </Button>
          </div>

          {groupedModules.length === 0 ? (
            <div className="icons-empty-state">
              <Empty description="Nessuna icona trovata" />
            </div>
          ) : (
            groupedModules.map((group) => (
              <div
                className="icons-category-section"
                key={group.category.objectId}
              >
                <div className="icons-category-title">
                  <div className="icons-category-main">
                    <span
                      className="icons-category-swatch"
                      style={{
                        backgroundColor: getCategoryTone(group.category)
                          .backgroundColor,
                      }}
                    />
                    <div className="icons-ellipsis">
                      <Text strong>{group.category.name}</Text>
                      <br />
                      <Text type="secondary">
                        {group.category.iconCount}/{group.category.moduleCount}{" "}
                        con icona
                      </Text>
                    </div>
                  </div>
                </div>
                <div className="icons-grid">
                  {group.modules.map(renderModuleCard)}
                </div>
              </div>
            ))
          )}
        </section>
      </div>

      <Modal
        title="Builder icona"
        open={builderModalOpen}
        onCancel={() => setBuilderModalOpen(false)}
        footer={null}
        width={1080}
        style={{ top: 24 }}
        className="icons-builder-modal"
      >
        {renderBuilderPanel()}
      </Modal>

      <Modal
        title="Genera icona AI"
        open={aiModalOpen}
        onCancel={() => setAiModalOpen(false)}
        footer={null}
        width={760}
        style={{ top: 24 }}
        className="icons-ai-modal"
      >
        <div className="icons-ai-wizard">
          <Steps
            className="icons-ai-steps"
            current={aiStep}
            onChange={setAiStep}
            responsive={false}
            items={AI_STEPS.map((step) => ({
              title: step.title,
              description: step.description,
            }))}
          />
          <div className="icons-ai-step-body">
            {aiStep === 0 && renderAiStepDescribe()}
            {aiStep === 1 && renderAiStepGenerate()}
            {aiStep === 2 && renderAiStepChoose()}
          </div>
        </div>
      </Modal>

      <Modal
        title="Applica a un modulo"
        open={applyModalOpen}
        onCancel={() => setApplyModalOpen(false)}
        footer={null}
        width={560}
        className="icons-apply-modal"
      >
        <div className="icons-apply-body">
          <Text type="secondary">
            Scegli il modulo a cui assegnare questa icona: viene caricata e
            salvata subito, senza scaricarla.
          </Text>

          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Cerca un modulo per nome..."
            value={applyTargetId}
            onChange={setApplyTargetId}
            style={{ width: "100%" }}
            notFoundContent="Nessun modulo"
            options={modules.map((module) => ({
              value: module.objectId,
              label: module.name,
              module,
            }))}
            optionRender={(option) => (
              <Space>
                <span className="icons-apply-opt-thumb">
                  {option.data.module?.iconUrl ? (
                    <img
                      src={option.data.module.iconUrl}
                      alt={option.data.label}
                      loading="lazy"
                    />
                  ) : (
                    <span className="icons-module-empty" />
                  )}
                </span>
                <span>{option.data.label}</span>
              </Space>
            )}
          />

          {applyTarget && (
            <div className="icons-apply-confirm">
              <div className="icons-apply-compare">
                <div className="icons-apply-side">
                  <Text type="secondary">Icona attuale</Text>
                  <div className="icons-apply-thumb">
                    {applyTarget.iconUrl ? (
                      <img src={applyTarget.iconUrl} alt="Icona attuale" />
                    ) : (
                      <span className="icons-apply-none">Nessuna</span>
                    )}
                  </div>
                </div>
                <span className="icons-apply-arrow">→</span>
                <div className="icons-apply-side">
                  <Text type="secondary">Nuova icona</Text>
                  <div
                    className="icons-apply-thumb"
                    dangerouslySetInnerHTML={{
                      __html: buildModuleIconSvg({
                        asset,
                        builder,
                        includeBackground: true,
                      }),
                    }}
                  />
                </div>
              </div>

              {applyTarget.iconUrl && (
                <Alert
                  type="warning"
                  showIcon
                  message={`“${applyTarget.name}” ha già un'icona`}
                  description="Confermando, l'icona attuale verrà sovrascritta in modo permanente."
                />
              )}
            </div>
          )}

          <div className="icons-apply-actions">
            <Button onClick={() => setApplyModalOpen(false)}>Annulla</Button>
            <Button
              type="primary"
              danger={Boolean(applyTarget?.iconUrl)}
              loading={applying}
              disabled={!applyTarget || !asset}
              onClick={applyIconToModule}
            >
              {applyTarget?.iconUrl ? "Sovrascrivi icona" : "Applica icona"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Icons;
